const Algo = {}

Algo.degrad = Math.PI / 180
Algo.raddeg = 180 / Math.PI

Algo.roundBy = function(n, decimals) {
  n = parseFloat(n)
  if(isNaN(n)) return n
  if(!(decimals>0)) return Math.round(n)
  return Number(n.toFixed(decimals))
}

Algo.roundRelative = function(n, significantDigits = 12) {
  if(n === 0) return 0
  const exponent = Math.floor(Math.log10(Math.abs(n)))
  const factor = Math.pow(10, exponent - significantDigits + 1)
  return Math.round(n / factor) * factor
} // roundRelative

Algo.dimensionsOf = function(src) {
  if(src instanceof CanvasRenderingContext2D) src = src.canvas
  if(src instanceof HTMLImageElement) {
    const { naturalWidth, naturalHeight } = src
    return [naturalWidth, naturalHeight]
  }else if(src instanceof HTMLVideoElement) {
    const { videoWidth, videoHeight } = src
    return [videoWidth, videoHeight]
  }else if(src instanceof HTMLCanvasElement) {
    const { width, height } = src
    return [width, height]
  }else if(src instanceof ImageData) {
    const { width, height } = src
    return [width, height]
  }else if(src instanceof Uint8Array || src instanceof Uint8ClampedArray) {
    const { width, height } = src
    return [width, height]
  }else if(typeof src==='object' && src.width>0 && src.height>0) {
    const { width, height } = src
    return [width, height]
  }

  return []
} // dimensionsOf

Algo.roundPoint = function([x, y]) {
  return [Math.round(x), Math.round(y)]
} // roundPoint

Algo.roundPoints = function(points) {
  return points.map(([x, y])=>[Math.round(x), Math.round(y)])
} // roundPoints

Algo.jpegFrom = async function(src, q=0.9, max_width=0, max_height=0, crop) {
  let canvas = src instanceof HTMLCanvasElement ? src : (await Algo.canvasFrom(src))?.[0]
  if(!(canvas instanceof HTMLCanvasElement)) return
  const { width, height } = canvas
  max_width = parseInt(max_width)
  max_height = parseInt(max_height)
  if(crop || max_width>0 || max_height>0) {
    let { x, y, w, h } = crop || {}
    x = parseInt(x)
    y = parseInt(y)
    w = parseInt(w)
    h = parseInt(h)
    if(isNaN(x)) x = 0
    if(isNaN(y)) y = 0
    if(isNaN(w)) w = width - x
    if(isNaN(h)) h = height - y

    let ratio
    if(max_width>0) {
      if(max_height>0) {
        ratio = Math.min(max_width / w, max_height / h)
      }else {
        ratio = max_width / w
      }
    }else {
      if(max_height>0) {
        ratio = max_height / h
      }else {
        ratio = 1
      }
    }
    const new_width = w * ratio
    const new_height = h * ratio

    const cropped = document.createElement('canvas')
    cropped.width = new_width
    cropped.height = new_height
    const ctx = cropped.getContext('2d')
    ctx.drawImage(canvas, x, y, w, h, 0, 0, new_width, new_height)
    canvas = cropped
  }
  const jpeg_url = canvas.toDataURL('image/jpeg', q)
  canvas = null
  return jpeg_url
} // jpegFrom

// create canvas from <video> or <img>
Algo.canvasFrom = async function(src, no_copy) {
  if(no_copy) {
    if(src instanceof HTMLCanvasElement) {
      const ctx = src.getContext('2d')
      return [src, ctx, src.width, src.height, src.width*src.height]
    }else if(src instanceof CanvasRenderingContext2D) {
      const canvas = src.canvas
      return [canvas, src, canvas.width, canvas.height, canvas.width*canvas.height]
    }
  }
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if(src instanceof CanvasRenderingContext2D) src = src.canvas
  if(src instanceof HTMLImageElement) {
    const { naturalWidth: width, naturalHeight: height } = src
    canvas.width = width
    canvas.height = height
    ctx.drawImage(src, 0, 0)
  }else if(src instanceof HTMLVideoElement) {
    const { videoWidth: width, videoHeight: height } = src
    canvas.width = width
    canvas.height = height
    ctx.drawImage(src, 0, 0)
  }else if(src instanceof HTMLCanvasElement) {
    const { width, height } = src
    canvas.width = width
    canvas.height = height
    ctx.drawImage(src, 0, 0)
  }else if(src instanceof ImageData) {
    const { width, height } = src
    canvas.width = width
    canvas.height = height
    ctx.putImageData(src, 0, 0)
  }else if(src instanceof Uint8Array || src instanceof Uint8ClampedArray) {
    const { width, height, length } = src
    canvas.width = width
    canvas.height = height
    const pixels = width*height
    const planes = length / pixels
    if(planes===1) {
      const idata = ctx.createImageData(width, height)
      const data = idata.data
      for(let i=0, j=0; i<length; i++, j+=4) {
        if(typeof src.rgb==='function') {
          [data[j], data[j+1], data[j+2]] = src.rgb(src[i])
        }else {
          data[j] = data[j+1] = data[j+2] = src[i]
        }
        data[j+3] = typeof src.opacity==='function' ? src.opacity(src[i]) : (src.opacity || 255)
      }
      ctx.putImageData(idata, 0, 0)
    }else if(planes===3) {
      const idata = ctx.createImageData(width, height)
      const data = idata.data
      for(let i=0, j=0; i<length; i+=3, j+=4) {
        data[j] = src[i]
        data[j+1] = src[i+1]
        data[j+2] = src[i+2]
        data[j+3] = typeof src.opacity==='function' ? src.opacity(src[i]) : (src.opacity || 255)
      }
      ctx.putImageData(idata, 0, 0)
    }else if(planes===4) {
      const idata = new ImageData(src, width, height)
      ctx.putImageData(idata, 0, 0)
    }else {
      throw new Error('Invalid planes')
    }
  }else if(src instanceof File) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          const { naturalWidth: width, naturalHeight: height } = img
          canvas.width = width
          canvas.height = height
          ctx.drawImage(img, 0, 0)
          resolve([canvas, ctx, width, height, width*height])
        }
        img.onerror = reject
        img.src = e.target.result
      }
      reader.readAsDataURL(src)
    })
  }else if(typeof src==='string') {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        const { naturalWidth: width, naturalHeight: height } = img
        canvas.width = width
        canvas.height = height
        ctx.drawImage(img, 0, 0)
        resolve([canvas, ctx, width, height, width*height])
      }
      img.onerror = reject
      img.src = src
    })
  }else if(typeof src==='object' && src.width>0 && src.height>0) {
    canvas.width = src.width
    canvas.height = src.height
  }else {
    throw new Error('Invalid source')
  }

  return [canvas, ctx, canvas.width, canvas.height, canvas.width*canvas.height]
} // canvasFrom

Algo.grayFrom = async function(src) {
  let data, width, height

  if(src instanceof HTMLCanvasElement) src = src.getContext('2d')
  if(src instanceof CanvasRenderingContext2D) src = src.getImageData(0, 0, src.canvas.width, src.canvas.height)
  if(src instanceof ImageData) {
    ({ data: src, width, height } = src)
  }

  if(src instanceof Uint8Array || src instanceof Uint8ClampedArray) {
    data = Algo.grayFromData(src, width, height)
  }else {
    const [canvas] = await Algo.canvasFrom(src)
    return await Algo.grayFrom(canvas)
  }

  data.width = width
  data.height = height
  return data
} // grayFrom

Algo.grayFromData = function(src, width, height) {
  if(isNaN(width)) width = src.width
  if(isNaN(height)) height = src.height
  const { length } = src
  const pixels = width*height
  const planes = length / pixels
  let data
  if(planes===1) {
    data = new Uint8ClampedArray(length)
    data.set(src)
  }else if(planes===3 || planes===4) {
    data = new Uint8ClampedArray(pixels)
    for(let i=0, j=0; i<length; i+=planes, j++) {
      data[j] = (src[i]*3 + src[i+1]*4 + src[i+2]) >> 3
    }
  }else {
    throw new Error('Invalid planes')
  }
  data.width = width
  data.height = height
  return data
} // grayFromData

Algo.rgbFrom = async function(src) {
  let data, width, height

  if(src instanceof HTMLCanvasElement) src = src.getContext('2d')
  if(src instanceof CanvasRenderingContext2D) src = src.getImageData(0, 0, src.canvas.width, src.canvas.height)
  if(src instanceof ImageData) {
    ({ data: src, width, height } = src)
  }

  if(src instanceof Uint8Array || src instanceof Uint8ClampedArray) {
    if(isNaN(width)) width = src.width
    if(isNaN(height)) height = src.height
    const { length } = src
    const pixels = width*height
    const planes = length / pixels
    const pixels3 = pixels*3
    if(planes===1) {
      data = new Uint8ClampedArray(pixels3)
      for(let i=0, j=0; j<pixels3; i++, j+=3) {
        if(typeof src.rgb==='function') {
          [data[j], data[j+1], data[j+2]] = src.rgb(src[i])
        }else {
          data[j] = data[j+1] = data[j+2] = src[i]
        }
      }
    }else if(planes===3) {
      data = new Uint8ClampedArray(pixels3)
      data.set(src)
    }else if(planes===4) {
      data = new Uint8ClampedArray(pixels3)
      for(let i=0, j=0; j<pixels3; i+=4, j+=3) {
        data[j] = src[i]
        data[j+1] = src[i+1]
        data[j+2] = src[i+2]
      }
    }else {
      throw new Error('Invalid planes')
    }
  }else {
    const [canvas] = await Algo.canvasFrom(src)
    return await Algo.rgbFrom(canvas)
  }

  data.width = width
  data.height = height
  return data
} // rgbFrom

Algo.rgbaFrom = async function(src) {
  let data, width, height

  if(src instanceof HTMLCanvasElement) src = src.getContext('2d')
  if(src instanceof CanvasRenderingContext2D) src = src.getImageData(0, 0, src.canvas.width, src.canvas.height)
  if(src instanceof ImageData) {
    ({ data: src, width, height } = src)
  }

  if(src instanceof Uint8Array || src instanceof Uint8ClampedArray) {
    if(isNaN(width)) width = src.width
    if(isNaN(height)) height = src.height
    const { length } = src
    const pixels = width*height
    const planes = length / pixels
    const pixels4 = pixels*4
    if(planes===1) {
      data = new Uint8ClampedArray(pixels4)
      for(let i=0, j=0; j<pixels4; i++, j+=4) {
        if(typeof src.rgb==='function') {
          [data[j], data[j+1], data[j+2]] = src.rgb(src[i])
        }else {
          data[j] = data[j+1] = data[j+2] = src[i]
        }
        data[j+3] = typeof src.opacity==='function' ? src.opacity(src[i]) : (src.opacity || 255)
      }
    }else if(planes===3) {
      data = new Uint8ClampedArray(pixels4)
      for(let i=0, j=0; j<pixels4; i+=3, j+=4) {
        data[j] = src[i]
        data[j+1] = src[i+1]
        data[j+2] = src[i+2]
        data[j+3] = typeof src.opacity==='function' ? src.opacity(src[i]) : (src.opacity || 255)
      }
    }else if(planes===4) {
      data = new Uint8ClampedArray(pixels4)
      data.set(src)
    }else {
      throw new Error('Invalid planes')
    }
  }else {
    const [canvas] = await Algo.canvasFrom(src)
    return await Algo.rgbaFrom(canvas)
  }

  data.width = width
  data.height = height
  return data
} // rgbaFrom

Algo.degreeDiff = function(theta1, theta2) {
  const diff = Math.abs(theta1 - theta2) % 360
  return Math.min(diff, 360 - diff)
} // degreeDiff

Algo.degreeAvg = function(...angles) {
  let sumX = 0
  let sumY = 0

  for(const angle of angles) {
    const angleRad = angle * Algo.degrad
    sumX += Math.cos(angleRad)
    sumY += Math.sin(angleRad)
  }

  const avgAngle = Math.atan2(sumY, sumX)
  return (avgAngle * Algo.raddeg + 360) % 360
} // degreeAvg

Algo.getContext = function(src) {
  if(src instanceof HTMLCanvasElement) {
    const ctx = src.getContext('2d', { alpha: true, willReadFrequently: true })
    return [src, ctx, src.width, src.height, src.width*src.height]
  }else if(src instanceof CanvasRenderingContext2D) {
    const canvas = src.canvas
    return [canvas, src, canvas.width, canvas.height, canvas.width*canvas.height]
  }else{
    return []
  }
} // getContext

Algo.getImageData = function(src, x, y, width, height) {
  if(src instanceof ImageData) {
    return src
  }else if(src instanceof HTMLCanvasElement) {
    if(!x) x = 0
    if(!y) y = 0
    if(!width) width = src.width
    if(!height) height = src.height
    const ctx = src.getContext('2d', { alpha: true, willReadFrequently: true })
    return ctx.getImageData(x, y, width, height)
  }else if(src instanceof CanvasRenderingContext2D) {
    if(!x) x = 0
    if(!y) y = 0
    if(!width) width = src.canvas.width
    if(!height) height = src.canvas.height
    return src.getImageData(x, y, width, height)
  }else{
    return {}
  }
} // getImageData

Algo.drawLine = function(src, x1, y1, x2, y2, color, linewidth) {
  const [, ctx] = Algo.getContext(src)
  ctx.beginPath()
  ctx.moveTo(x1, y1)
  ctx.lineTo(x2, y2)
  ctx.strokeStyle = color
  ctx.lineWidth = linewidth || 1
  ctx.stroke()
} // drawLine

Algo.drawCross = function(src, x, y, size, color, linewidth=1) {
  const [, ctx] = Algo.getContext(src)
  ctx.beginPath()
  ctx.moveTo(x-size, y)
  ctx.lineTo(x+size, y)
  ctx.moveTo(x, y-size)
  ctx.lineTo(x, y+size)
  ctx.strokeStyle = color
  ctx.lineWidth = linewidth
  ctx.stroke()
} // drawCross

Algo.drawCircle = function(src, x, y, r, bg, color, linewidth) {
  const [, ctx] = Algo.getContext(src)
  ctx.beginPath()
  ctx.arc(x, y, r, 0, 2 * Math.PI, false)
  if(bg) {
    ctx.fillStyle = bg
    ctx.fill()
  }
  if(color) {
    ctx.strokeStyle = color
    ctx.lineWidth = linewidth || 1
    ctx.stroke()
  }
} // drawCircle

Algo.drawEllipse = function(src, x, y, a, b, bg, color, linewidth) {
  const [, ctx] = Algo.getContext(src)
  ctx.beginPath()
  ctx.ellipse(x, y, a, b, 0, 0, 2 * Math.PI)
  if(bg) {
    ctx.fillStyle = bg
    ctx.fill()
  }
  if(color) {
    ctx.strokeStyle = color
    ctx.lineWidth = linewidth || 1
    ctx.stroke()
  }
} // drawCircle

Algo.drawPoints = function(src, points, color) {
  const [, ctx] = Algo.getContext(src)
  ctx.fillStyle = color
  for(const [x, y] of points) ctx.fillRect(x, y, 1, 1)
} // drawPoints

Algo.drawText = function(src, text, size, x, y, align, bg, fg, linewidth=1, font='Helvetica') {
  const [, ctx] = Algo.getContext(src)

  ctx.font = size+'px '+font
  ctx.textAlign = align || 'center'
  ctx.textBaseline = 'middle'

  if(bg) {
    ctx.fillStyle = bg
    ctx.fillText(text, x, y)
  }
  if(fg) {
    ctx.strokeStyle = fg
    ctx.lineWidth = linewidth
    ctx.strokeText(text, x, y)
  }
} // drawText

Algo.fillCanvas = function(src, color='#fff') {
  const [canvas, ctx] = Algo.getContext(src)
  ctx.fillStyle = color
  ctx.fillRect(0, 0, canvas.width, canvas.height)
} // fillCanvas

Algo.canvas2rgb = function(src) {
  const { data, width, height } = Algo.getImageData(src)
  if(!data) return
  const size = width*height
  const dst = new Uint8Array(size*3)
  const size4 = size*4
  for(let i=0, j=0; i<size4; i+=4, j+=3) {
    dst[j] = data[i]
    dst[j+1] = data[i+1]
    dst[j+2] = data[i+2]
  }
  dst.width = width
  dst.height = height
  return dst
} // canvas2rgb

Algo.canvas2sl = function(src, x, y, new_width, new_height) {
  // construct two matrices of saturation and lightning
  const { data, width, height } = Algo.getImageData(src, x, y, new_width, new_height)
  if(!data) return
  const size = width*height
  const saturation = new Uint8Array(size)
  const lightness = new Uint8Array(size)
  const size4 = size*4
  for(let i=0, j=0; i<size4; i+=4, j++) {
    const max = Math.max(data[i], data[i+1], data[i+2])
    const min = Math.min(data[i], data[i+1], data[i+2])
    const l = lightness[j] = Math.round((max + min) / 2)
    saturation[j] = max===min ? 0 : Math.round(255 * (max - min) / (l > 127 ? (510 - max - min) : (max + min)))
  }
  saturation.width = lightness.width = width
  saturation.height = lightness.height = height
  return [saturation, lightness]
} // canvas2sl

Algo.canvas2bwx = function(src) {
  // construct matrix from canvas RGB data
  // 0 are near-black points
  // 255 are near-white points
  // 128 are the rest
  const { data, width, height } = Algo.getImageData(src)
  if(!data) return
  const size = width*height
  const dst = new Uint8Array(size)
  const size4 = size*4
  for(let i=0, j=0; i<size4; i+=4, j++) {
    const max = Math.max(data[i], data[i+1], data[i+2])
    const min = Math.min(data[i], data[i+1], data[i+2])
    const l = (max + min) / 2
    const s = max===min ? 0 : Math.round(255 * (max - min) / (l > 127 ? (510 - max - min) : (max + min)))
    if(l>240) {
      dst[j] = s<32 ? 255 : 128
    }else if(l>140) {
      // near white
      dst[j] = s<64 ? 255 : 128
    }else if(l<16) {
      dst[j] = 0
    }else if(l<120) {
      // near black
      dst[j] = s<32 ? 0 : 128
    }else {
      dst[j] = s<16 ? 0 : 128
    }
  }
  dst.width = width
  dst.height = height
  return dst
} // canvas2bwx

Algo.rgb2canvas = function(src, dst) {
  if(!(src instanceof Uint8Array) || !src.width || !src.height) return
  const width = parseInt(src.width)
  const height = parseInt(src.height)

  let [canvas, ctx] = Algo.getContext(dst)
  if(canvas) dst = ctx.createImageData(width, height)
  if(!(dst instanceof ImageData)) return
  if(canvas.width !== width || canvas.height !== height) {
    canvas.width = width
    canvas.height = height
    ctx = Algo.getContext(canvas)[1]
  }

  const data = dst.data
  const size3 = width*height*3
  for(let i=0, j=0; i<size3; i+=3, j+=4) {
    data[j] = src[i]
    data[j+1] = src[i+1]
    data[j+2] = src[i+2]
    data[j+3] = 255
  }
  if(ctx) ctx.putImageData(dst, 0, 0)
  return dst
} // rgb2canvas

Algo.canvas2gray = function(src, x, y, new_width, new_height) {
  const { data, width, height } = Algo.getImageData(src, x, y, new_width, new_height)
  if(!data) return
  const size = width*height
  const dst = new Uint8Array(size)
  const size4 = size*4
  for(let i=0, j=0; i<size4; i+=4, j++) {
    dst[j] = (data[i]*3 + data[i+1]*4 + data[i+2]) >> 3
  }
  dst.width = width
  dst.height = height
  return dst
} // canvas2gray

Algo.gray2canvas = function(src, dst) {
  if(!(src instanceof Uint8Array) || !src.width || !src.height) return
  const width = parseInt(src.width)
  const height = parseInt(src.height)

  let [canvas, ctx] = Algo.getContext(dst)
  if(canvas) dst = ctx.createImageData(width, height)
  if(!(dst instanceof ImageData)) return
  if(canvas.width !== width || canvas.height !== height) {
    canvas.width = width
    canvas.height = height
    ctx = Algo.getContext(canvas)[1]
  }

  const data = dst.data
  const size = width*height
  for(let i=0, j=0; i<size; i++, j+=4) {
    data[j] = data[j+1] = data[j+2] = src[i]
    data[j+3] = 255
  }
  if(ctx) ctx.putImageData(dst, 0, 0)
  return dst
} // gray2canvas

Algo.bwx2canvas = function(src, dst) {
  if(!(src instanceof Uint8Array) || !src.width || !src.height) return
  const width = parseInt(src.width)
  const height = parseInt(src.height)

  let [canvas, ctx] = Algo.getContext(dst)
  if(canvas) dst = ctx.createImageData(width, height)
  if(!(dst instanceof ImageData)) return
  if(canvas.width !== width || canvas.height !== height) {
    canvas.width = width
    canvas.height = height
    ctx = Algo.getContext(canvas)[1]
  }

  const data = dst.data
  const size = width*height
  for(let i=0, j=0; i<size; i++, j+=4) {
    if(src[i]===128) {
      data[j] = 255
      data[j+1] = data[j+2] = 0
    }else {
      data[j] = data[j+1] = data[j+2] = src[i]
    }
    data[j+3] = 255
  }
  if(ctx) ctx.putImageData(dst, 0, 0)
  return dst
} // bwx2canvas

Algo.extract = function(src, x, y, new_width, new_height) {
  const { width, height } = src
  const size = new_width*new_height
  const dst = new Uint8Array(size)

  if(!(x>=0)) x=0
  if(!(y>=0)) y=0
  x = Math.round(x)
  y = Math.round(y)
  if(x>width) x = width
  if(y>height) y = height
  new_width = Math.round(new_width)
  new_height = Math.round(new_height)
  if(new_width<=0) new_width = width+new_width
  if(new_height<=0) new_height = height+new_height
  if(!(new_width>=0)) new_width=0
  if(!(new_height>=0)) new_height=0
  if(x+new_width>width) new_width = width-x
  if(y+new_height>height) new_height = height-y

  for(let dy=0; dy<new_height; dy++) {
    const offset = (y+dy)*width+x
    dst.set(src.subarray(offset, offset+new_width), dy*new_width)
  }
  dst.width = new_width
  dst.height = new_height
  return dst
} // extract

Algo.adaptiveContrast = function(src) {
  const { width, height } = src
  const planes = src.length / (width * height)
  if(planes === 1) return Algo.adaptiveContrastGray(src)
  if(planes === 3 || planes === 4) return Algo.adaptiveContrastRGB(src)
  if(src.length === width * height * 3) return Algo.adaptiveContrastRGB(src)
  return src
} // adaptiveContrast

Algo.adaptiveContrastGray = function(src) {
  const { width, height } = src

  // calculate histogram
  const histogram = new Array(256).fill(0)
  for(let i=0; i<src.length; i++) {
    histogram[src[i]]++
  }

  // compute cumulative distribution function (CDF)
  const cdf = new Array(256).fill(0)
  cdf[0] = histogram[0]
  for(let i=1; i < histogram.length; i++) {
    cdf[i] = cdf[i - 1] + histogram[i]
  }

  // normalize CDF
  const cdfMin = cdf.find(value => value > 0)
  const totalPixels = width * height
  const cdfNormalized = cdf.map(value => ((value - cdfMin) / (totalPixels - cdfMin)) * 255)

  // apply new values to create the contrast-enhanced image
  const dst = new Uint8ClampedArray(src.length)
  for(let i=0; i<src.length; i++) {
    dst[i] = cdfNormalized[src[i]]
  }

  dst.width = width
  dst.height = height
  return dst
} // adaptiveContrastGray

Algo.adaptiveContrastRGB = function(src) {
  const { width, height } = src
  const size = width * height
  const planes = src.length / size

  // separate channels
  const red = new Uint8ClampedArray(size)
  const green = new Uint8ClampedArray(size)
  const blue = new Uint8ClampedArray(size)
  for(let i=0; i<size; i++) {
    const j = i*planes
    red[i] = src[j]
    green[i] = src[j + 1]
    blue[i] = src[j + 2]
  }

  // process each channel
  red.width = green.width = blue.width = width
  red.height = green.height = blue.height = height
  const red2 = Algo.adaptiveContrast(red)
  const green2 = Algo.adaptiveContrast(green)
  const blue2 = Algo.adaptiveContrast(blue)

  // combine channels back to RGB array
  const dst = new Uint8ClampedArray(size*planes)
  for(let i=0; i<size; i++) {
    const j = i*planes
    dst[j] = red2[i]
    dst[j + 1] = green2[i]
    dst[j + 2] = blue2[i]
    if(planes===4) dst[j + 3] = 255
  }

  dst.width = width
  dst.height = height
  return dst
} // adaptiveContrastRGB

Algo.adaptiveMeanThreshold = function(src, windowSize) {
  const { width, height } = src
  if(!(windowSize>0)) windowSize = Math.floor(height/8)
  const dst = new Uint8Array(width * height)
  const halfWindow = Math.floor(windowSize / 2)

  for(let y = 0; y < height; y++) {
    for(let x = 0; x < width; x++) {
      let sum = 0
      let count = 0

      // Calculate the mean of the neighboring pixels
      for(let j=-halfWindow; j<=halfWindow; j++) {
        for(let i=-halfWindow; i<=halfWindow; i++) {
          const newX = x + i
          const newY = y + j
          if (newX >= 0 && newX < width && newY >= 0 && newY < height) {
            const idx = newY * width + newX
            sum += src[idx]
            count++
          }
        }
      }

      const mean = sum / count
      const idx = y * width + x

      // Thresholding
      dst[idx] = src[idx] > mean ? 255 : 0
    }
  }

  dst.width = src.width
  dst.height = src.height
  return dst
} // adaptiveMeanThreshold

Algo.adaptiveGaussianThreshold = function(src, windowSize, C) {
  const { width, height } = src
  if(!(windowSize>0)) windowSize = Math.floor(height/8)
  const dst = new Uint8Array(width * height)
  const halfWindow = Math.floor(windowSize / 2)

  for(let y = 0; y < height; y++) {
    for(let x = 0; x < width; x++) {
      let sum = 0; let sumSq = 0; let count = 0

      // Calculate mean and variance of the neighboring pixels
      for (let j = -halfWindow; j <= halfWindow; j++) {
        for (let i = -halfWindow; i <= halfWindow; i++) {
          const newX = x + i
          const newY = y + j

          if (newX >= 0 && newX < width && newY >= 0 && newY < height) {
            const idx = newY * width + newX
            sum += src[idx]
            sumSq += src[idx] * src[idx]
            count++
          }
        }
      }

      const mean = sum / count
      const variance = (sumSq - mean * mean * count) / (count - 1)
      const stdDev = Math.sqrt(variance)

      const idx = y * width + x

      // Thresholding
      const threshold = mean * (1 + C * (stdDev / 128 - 1))
      dst[idx] = src[idx] > threshold ? 0 : 255
    }
  }

  dst.width = src.width
  dst.height = src.height
  return dst
} // adaptiveGaussianThreshold

Algo.meanVarDev = function(src) {
  let sum = 0
  let sumSq = 0
  const count = src.length
  for(let p=0; p<count; p++) {
    sum += src[p]
    sumSq += src[p]**2
  }

  const mean = sum / count
  const variance = (sumSq - mean * mean * count) / (count - 1)
  const deviation = Math.sqrt(variance) // standard deviation
  return { mean, variance, deviation }
} // meanVarDev

Algo.sl2bwx = function(saturation, lightness) {
  const { width, height } = saturation
  const size = width*height
  const dst = new Uint8Array(size)
  for(let i=0; i<size; i++) {
    const l = lightness[i]
    const s = saturation[i]
    if(l>240) {
      dst[i] = 255
    }else if(l>180) {
      // near white
      dst[i] = s<128 ? 255 : 128
      // dst[i] = 255
    }else if(l>140) {
      // near white
      dst[i] = s<64 ? 255 : 128
      // dst[i] = 255
    }else if(l<16) {
      dst[i] = 0
    }else if(l<120) {
      // near black
      dst[i] = s<32 ? 0 : 128
    }else {
      dst[i] = s<32 ? 0 : 128
    }
  }
  dst.width = width
  dst.height = height
  return dst
} // sl2bwx

Algo.bwxSmooth = function(src, dst) {
  if(!dst || dst.width!==src.width || dst.height!==src.height) {
    dst = new Uint8Array(src.width*src.height)
    dst.width = src.width
    dst.height = src.height
  }

  const width = src.width
  const height = src.height
  const width1 = width-1
  const width2 = width+1
  const height1 = height-1

  // at the image border just copy pixels
  const last_line_offset = width*(height-1)
  for(let x=0; x<width; x++) {
    dst[x] = src[x]
    dst[last_line_offset+x] = src[last_line_offset+x]
  }
  let first_col_start = 0
  for(let y=0; y<height; y++) {
    dst[first_col_start] = src[first_col_start]
    dst[first_col_start+width1] = src[first_col_start+width1]
    first_col_start += width
  }

  // calculate 3x3 Gauss for all other pixels
  let offset = width+1
  for(let y=1; y<height1; y++) {
    for(let x=1; x<width1; x++) {
      if(src[offset]===128) {
        const sum =
          src[offset-width2] + src[offset-width] + src[offset-width1] +
          src[offset-1] + src[offset+1] +
          src[offset+width1] + src[offset+width] + src[offset+width2]
        dst[offset] = sum>=255*6 ? 255 : 128
      }else {
        dst[offset] = src[offset]
      }
      offset++
    }
    offset+=2
  }

  return dst
} // bwxSmooth

Algo.threshold = function(src, dst, threshold) {
  if(!dst || dst.width!==src.width || dst.height!==src.height) {
    dst = new Uint8Array(src.width*src.height)
    dst.width = src.width
    dst.height = src.height
  }

  const count = src.width * src.height
  if(threshold>0) {
    for(let p=0; p<count; p++) {
      dst[p] = src[p]>threshold ? 255 : 0
    }
  }else if(threshold<0) {
    threshold = -threshold
    for(let p=0; p<count; p++) {
      dst[p] = src[p]>threshold ? 0 : 255
    }
  }

  return dst
} // threshold

Algo.gauss9 = function(src, dst) {
  if(!dst || dst.width!==src.width || dst.height!==src.height) {
    dst = new Uint8Array(src.width*src.height)
    dst.width = src.width
    dst.height = src.height
  }

  const width = src.width
  const height = src.height
  const width1 = width-1
  const width2 = width+1
  const height1 = height-1

  // at the image border just copy pixels
  const last_line_offset = width*(height-1)
  for(let x=0; x<width; x++) {
    dst[x] = src[x]
    dst[last_line_offset+x] = src[last_line_offset+x]
  }
  let first_col_start = 0
  for(let y=0; y<height; y++) {
    dst[first_col_start] = src[first_col_start]
    dst[first_col_start+width1] = src[first_col_start+width1]
    first_col_start += width
  }

  // calculate 3x3 Gauss for all other pixels
  let offset = width+1
  for(let y=1; y<height1; y++) {
    for(let x=1; x<width1; x++) {
      const sum =
        src[offset-width2] + 2*src[offset-width] + src[offset-width1] +
        2*src[offset-1] + 4*src[offset] + 2*src[offset+1] +
        src[offset+width1] + 2*src[offset+width] + src[offset+width2]
      dst[offset] = Math.floor(sum/16)
      offset++
    }
    offset+=2
  }

  return dst
} // gauss9

Algo.median = function(src, dst) {
  if(!dst || dst.width!==src.width || dst.height!==src.height) {
    dst = new Uint8Array(src.width*src.height)
    dst.width = src.width
    dst.height = src.height
  }

  const width = src.width
  const height = src.height
  const width1 = width-1
  const width2 = width+1
  const height1 = height-1

  // at the image border just copy pixels
  const last_line_offset = width*(height-1)
  for(let x=0; x<width; x++) {
    dst[x] = src[x]
    dst[last_line_offset+x] = src[last_line_offset+x]
  }
  let first_col_start = 0
  for(let y=0; y<height; y++) {
    dst[first_col_start] = src[first_col_start]
    dst[first_col_start+width1] = src[first_col_start+width1]
    first_col_start += width
  }

  // calculate 3x3 Gauss for all other pixels
  let offset = width+1
  for(let y=1; y<height1; y++) {
    for(let x=1; x<width1; x++) {
      let values = [
        src[offset-width2], src[offset-width], src[offset-width1],
        src[offset-1], src[offset], src[offset+1],
        src[offset+width1], src[offset+width], src[offset+width2],
      ]
      values = values.sort()
      dst[offset] = values[4]
      offset++
    }
    offset+=2
  }

  return dst
} // median

Algo.gauss9multi = function(src) {
  const { width, height } = src
  const planes = src.length / width / height
  const lwidth = width*planes
  const pixels = lwidth*height
  const dst = new Uint8ClampedArray(pixels)
  dst.width = width
  dst.height = height

  // at the image border just copy pixels
  dst.set(src.subarray(0, lwidth), 0)
  const last_line_offset = lwidth*(height-1)
  dst.set(src.subarray(last_line_offset, lwidth), last_line_offset)
  for(let offset=0; offset<pixels; offset+=lwidth) {
    const a = offset
    const b = a + lwidth - planes
    dst.set(src.subarray(a, a+planes), a)
    dst.set(src.subarray(b, b+planes), b)
  }

  // calculate 3x3 Gauss for all other pixels on all planes
  const width0 = planes*(width)
  const width1 = planes*(width-1)
  const width2 = planes*(width+1)
  let offset = planes*(width+1)
  const line_change = planes*2
  for(let y=1; y<height-1; y++, offset+=line_change) {
    for(let x=1; x<width-1; x++) {
      for(let i=0; i<planes; i++, offset++) {
        if(i===3) {
          dst[offset] = 255
        }else{
          const sum =
            src[offset-width2] + 2*src[offset-width0] + src[offset-width1] +
            2*src[offset-planes] + 4*src[offset] + 2*src[offset+planes] +
            src[offset+width1] + 2*src[offset+width0] + src[offset+width2]
          dst[offset] = Math.floor(sum/16)
        }
      }
    }
  }

  return dst
} // gauss9multi

Algo.sobel = function(src, dst, threshold=160) {
  if(!dst || dst.width!==src.width || dst.height!==src.height) {
    dst = new Uint8Array(src.width*src.height)
    dst.width = src.width
    dst.height = src.height
  }

  threshold = threshold>0 ? threshold : 0
  const threshold2 = threshold*threshold // make it faster to limit SQRT operations

  const width = src.width
  const height = src.height
  const width1 = width-1
  const width2 = width+1
  const height1 = height-1

  // at the image border just clear pixels
  const last_line_offset = width*(height-1)
  for(let x=0; x<width; x++) {
    dst[x] = 0
    dst[last_line_offset+x] = 0
  }
  let first_col_start = 0
  for(let y=0; y<height; y++) {
    dst[first_col_start] = 0
    dst[first_col_start+width1] = 0
    first_col_start += width
  }

  // calculate 3x3 Sobel for all other pixels
  let offset = width+1
  for(let y=1; y<height1; y++) {
    for(let x=1; x<width1; x++) {
      const gx = (
        -src[offset-width2] + src[offset-width1] -
        2*src[offset-1] + 2*src[offset+1] -
        src[offset+width1] + src[offset+width2]
      )/4
      const gy = (
        +src[offset-width2] + 2*src[offset-width] + src[offset-width1] -
        src[offset+width1] - 2*src[offset+width] - src[offset+width2]
      )/4
      const g2 = gx*gx+gy*gy
      dst[offset] = threshold2 ? (g2>=threshold2 ? 0 : 255) : Math.round(Math.sqrt(g2))
      offset++
    }
    offset+=2
  }

  return dst
} // sobel

Algo.sobelXY = function(src, dst) {
  if(!dst || dst.width!==src.width || dst.height!==src.height) {
    dst = new Array(src.width*src.height)
    dst.width = src.width
    dst.height = src.height
  }

  const width = src.width
  const height = src.height
  const width1 = width-1
  const width2 = width+1
  const height1 = height-1

  // at the image border just clear pixels
  const last_line_offset = width*(height-1)
  for(let x=0; x<width; x++) {
    dst[x] = [0, 0]
    dst[last_line_offset+x] = [0, 0]
  }
  let first_col_start = 0
  for(let y=0; y<height; y++) {
    dst[first_col_start] = [0, 0]
    dst[first_col_start+width1] = [0, 0]
    first_col_start += width
  }

  // calculate 3x3 Sobel for all other pixels
  let offset = width+1
  for(let y=1; y<height1; y++) {
    for(let x=1; x<width1; x++) {
      const gx = (
        -src[offset-width2] + src[offset-width1] -
        2*src[offset-1] + 2*src[offset+1] -
        src[offset+width1] + src[offset+width2]
      )/4
      const gy = (
        +src[offset-width2] + 2*src[offset-width] + src[offset-width1] -
        src[offset+width1] - 2*src[offset+width] - src[offset+width2]
      )/4
      dst[offset] = [gx, gy]
      offset++
    }
    offset+=2
  }

  return dst
} // sobelXY

Algo.harris = function(src, dst, k = 0.04, threshold = 1e6) {
  if(!dst || dst.width!==src.width || dst.height!==src.height) {
    dst = new Uint8Array(src.width*src.height)
    dst.width = src.width
    dst.height = src.height
  }

  // Sobel operator
  const sobel = Algo.sobelXY(src)

  // Harris corner detection
  dst.fill(0)
  const windowSize = 3
  const offset = Math.floor(windowSize / 2)
  const { width, height } = src
  const height1 = height - offset
  const width1 = width - offset

  for(let y = offset; y < height1; y++) {
    for(let x = offset; x < width1; x++) {
      let sumIx2 = 0
      let sumIy2 = 0
      let sumIxy = 0

      for(let wy = -offset; wy <= offset; wy++) {
        for(let wx = -offset; wx <= offset; wx++) {
          const i = (y + wy) * width + (x + wx)
          const [ix, iy] = sobel[i]
          sumIx2 += ix * ix
          sumIy2 += iy * iy
          sumIxy += ix * iy
        }
      }

      const det = sumIx2 * sumIy2 - sumIxy * sumIxy
      const trace = sumIx2 + sumIy2
      const r = det - k * trace * trace

      if(r > threshold) {
        dst[y * width + x] = 255
      }
    }
  }

  return dst
} // harris

Algo.findChessboardCorners = function(gray, k = 0.01, threshold = 1e9, min_size = 5, max_size = 100) {
  const gauss = Algo.gauss9(gray)
  const harris = Algo.harris(gauss, null, k, threshold)
  let corners = Algo.findClustersBoolean(harris, min_size, max_size)

  const { width, height } = gray
  const cx = width / 2
  const cy = height / 2

  // calculate distance from center of image for each corner
  for(const corner of corners) {
    const dx = corner.xavg - cx
    const dy = corner.yavg - cy
    const d = Math.sqrt(dx*dx + dy*dy)
    Object.assign(corner, { dx, dy, d })
  }

  // sort corners by distance from center
  corners = corners.sort((a, b) => a.d - b.d)

  // estimate cell size as smallest distance between
  // corner closest to center and the other two closest corners
  const mid = corners[0]
  const mid2 = corners[1]
  const mid3 = corners[2]
  const diff1 = Math.sqrt((mid.xavg - mid2.xavg) ** 2 + (mid.yavg - mid2.yavg) ** 2)
  const diff2 = Math.sqrt((mid.xavg - mid3.xavg) ** 2 + (mid.yavg - mid3.yavg) ** 2)
  const cell_size = Math.min(diff1, diff2)
  const tolerance = cell_size * 0.2

  // iteratively assign position in a grid relative to mid corner to each corner
  mid.rx = 0
  mid.ry = 0

  const indexed = []
  const sibling_max_distance_sq = (cell_size + tolerance) ** 2
  while(corners.length > 0) {
    const corner = corners.shift()
    if(isNaN(corner.rx)) continue
    const { rx, ry, xavg, yavg } = corner
    indexed.push({ x: xavg, y: yavg, rx, ry })
    for(let i=0; i<corners.length; i++) {
      const c = corners[i]
      const dx = c.xavg - xavg
      const dy = c.yavg - yavg
      if(dx*dx + dy*dy < sibling_max_distance_sq) {
        // sibling
        if(Math.abs(dx) > Math.abs(dy)) {
          // horizontal sibling
          c.ry = ry
          c.rx = dx > 0 ? rx + 1 : rx - 1
        }else {
          // vertical sibling
          c.rx = rx
          c.ry = dy > 0 ? ry + 1 : ry - 1
        }

        // ensure identified corner will be put upfront
        if(isNaN(corners[0].rx)) {
          const tmp = corners[0]
          corners[0] = corners[i]
          corners[i] = tmp
        }
      }
    }
  }

  return indexed
} // findChessboardCorners

Algo.sobel5 = function(src, dst, threshold=160) {
  if(!dst || dst.width!==src.width || dst.height!==src.height) {
    dst = new Uint8Array(src.width*src.height)
    dst.width = src.width
    dst.height = src.height
  }

  const width = src.width
  const height = src.height

  threshold = threshold>0 ? threshold : 0
  const threshold2 = threshold*threshold // make it faster to limit SQRT operations

  // at the image border just clear pixels
  for(const y of [0, 1, height-2, height-1]) {
    dst.fill(0, y*width, (y+1)*width)
  }
  for(let y=0; y<height; y++) {
    const o = width*y
    for(const x of [0, 1, width-2, width-1]) {
      dst[o+x] = 0
    }
  }

  // calculate 5x5 Sobel for all other pixels
  for(let y=2; y<height-2; y++) {
    const o = y*width
    for(let x=2; x<width-2; x++) {
      const x3 = o + x
      const x2 = x3-width
      const x1 = x2-width
      const x4 = x3+width
      const x5 = x4+width
      const gx = (
        +2 * src[x1-2] +1 * src[x1-1] +0 * src[x1] -1 * src[x1+1] -2 * src[x1+2]+
        2 * src[x2-2] +1 * src[x2-1] +0 * src[x2] -1 * src[x2+1] -2 * src[x2+2]+
        4 * src[x3-2] +2 * src[x3-1] +0 * src[x3] -2 * src[x3+1] -4 * src[x3+2]+
        2 * src[x4-2] +1 * src[x4-1] +0 * src[x4] -1 * src[x4+1] -2 * src[x4+2]+
        2 * src[x5-2] +1 * src[x5-1] +0 * src[x5] -1 * src[x5+1] -2 * src[x5+2]
      ) / 18
      const gy = (
        +2 * src[x1-2] +2 * src[x1-1] +4 * src[x1] +2 * src[x1+1] +2 * src[x1+2]+
        1 * src[x2-2] +1 * src[x2-1] +2 * src[x2] +1 * src[x2+1] +1 * src[x2+2]-
        // +0 * src[x3-2] +0 * src[x3-1] +0 * src[x3] +0 * src[x3+1] +0 * src[x3+2]
        1 * src[x4-2] -1 * src[x4-1] -2 * src[x4] -1 * src[x4+1] -1 * src[x4+2]-
        2 * src[x5-2] -2 * src[x5-1] -4 * src[x5] -2 * src[x5+1] -2 * src[x5+2]
      ) / 18

      const g2 = gx**2 + gy**2
      dst[x3] = threshold2 ? (g2>=threshold2 ? 0 : 255) : Math.round(Math.sqrt(g2))
    }
  }

  return dst
} // sobel5

Algo.sobelD = function(src, dst, threshold=160) {
  if(!dst || dst.width!==src.width || dst.height!==src.height) {
    dst = new Uint8Array(src.width*src.height)
    dst.width = src.width
    dst.height = src.height
  }

  const width = src.width
  const height = src.height

  threshold = threshold>0 ? threshold : 0

  // at the image border just clear pixels
  for(const y of [0, 1, height-2, height-1]) {
    dst.fill(0, y*width, (y+1)*width)
  }
  for(let y=0; y<height; y++) {
    const o = width*y
    for(const x of [0, 1, width-2, width-1]) {
      dst[o+x] = 0
    }
  }

  // calculate 5x5 Sobel for all other pixels
  for(let y=2; y<height-2; y++) {
    const o = y*width
    for(let x=2; x<width-2; x++) {
      const x3 = o + x
      const x2 = x3-width
      const x1 = x2-width
      const x4 = x3+width
      const x5 = x4+width
      let val = (
        +6 * src[x1-2] +5 * src[x1-1] +4 * src[x1] +2 * src[x1+1] +2 * src[x1+2]+
        4 * src[x2-2] +3 * src[x2-1] +2 * src[x2] +1 * src[x2+1] +0 * src[x2+2]+
        2 * src[x3-2] +1 * src[x3-1] +0 * src[x3] -1 * src[x3+1] -2 * src[x3+2]-
        0 * src[x4-2] -1 * src[x4-1] -2 * src[x4] -3 * src[x4+1] -4 * src[x4+2]-
        2 * src[x5-2] -3 * src[x5-1] -4 * src[x5] -5 * src[x5+1] -6 * src[x5+2]
      ) / 33

      val = Math.max(0, val)
      val = Math.round(val)
      dst[x3] = threshold>0 ? (val>=threshold?255:0) : val
    }
  }

  return dst
} // sobelD

Algo.sobelD5 = function(src, dst, threshold=160) {
  if(!dst || dst.width!==src.width || dst.height!==src.height) {
    dst = new Uint8Array(src.width*src.height)
    dst.width = src.width
    dst.height = src.height
  }

  const width = src.width
  const height = src.height

  // at the image border just clear pixels
  for(const y of [0, 1, height-2, height-1]) {
    dst.fill(0, y*width, (y+1)*width)
  }
  for(let y=0; y<height; y++) {
    const o = width*y
    for(const x of [0, 1, width-2, width-1]) {
      dst[o+x] = 0
    }
  }

  // calculate 5x5 Sobel for all other pixels
  for(let y=2; y<height-2; y++) {
    const o = y*width
    for(let x=2; x<width-2; x++) {
      const x3 = o + x
      const x2 = x3-width
      const x1 = x2-width
      const x4 = x3+width
      const x5 = x4+width
      let val = (
        +1 * src[x1-2] +1 * src[x1-1] +1 * src[x1] +1 * src[x1+1] +0 * src[x1+2]+
        1 * src[x2-2] +1 * src[x2-1] +1 * src[x2] +0 * src[x2+1] -1 * src[x2+2]+
        1 * src[x3-2] +1 * src[x3-1] +0 * src[x3] -1 * src[x3+1] -1 * src[x3+2]+
        1 * src[x4-2] +0 * src[x4-1] -1 * src[x4] -1 * src[x4+1] -1 * src[x4+2]+
        0 * src[x5-2] -1 * src[x5-1] -1 * src[x5] -1 * src[x5+1] -1 * src[x5+2]

        // +16 * src[x1-2] +4 * src[x1-1] +2 * src[x1] +1 * src[x1+1] +0 * src[x1+2]
        // +4 * src[x2-2] +2 * src[x2-1] +1 * src[x2] +0 * src[x2+1] -1 * src[x2+2]
        // +2 * src[x3-2] +1 * src[x3-1] +0 * src[x3] -1 * src[x3+1] -2 * src[x3+2]
        // +1 * src[x4-2] +0 * src[x4-1] -1 * src[x4] -2 * src[x4+1] -4 * src[x4+2]
        // +0 * src[x5-2] -1 * src[x5-1] -2 * src[x5] -4 * src[x5+1] -16 * src[x5+2]

        // left to right diagonal
        // -2 * src[x1-2] -1 * src[x1-1] +1 * src[x1+1] +2 * src[x1+2]
        // -1 * src[x2-2] -1 * src[x2-1] +1 * src[x2+1] +1 * src[x2+2]
        // +1 * src[x4-2] +1 * src[x4-1] -1 * src[x4+1] -1 * src[x4+2]
        // +2 * src[x5-2] +1 * src[x5-1] -1 * src[x5+1] -2 * src[x5+2]

        // left to right diagonal between white and black
        // +2 * src[x1-2] +1 * src[x1-1] -1 * src[x1+1] -2 * src[x1+2]
        // +1 * src[x2-2] +2 * src[x2-1] -2 * src[x2+1] -1 * src[x2+2]
        // -1 * src[x4-2] -2 * src[x4-1] +2 * src[x4+1] +1 * src[x4+2]
        // -2 * src[x5-2] -1 * src[x5-1] +1 * src[x5+1] +2 * src[x5+2]
      )
      val = Math.max(0, val)
      // val = Math.abs(val)
      val = Math.round(val / 10)
      dst[x3] = threshold>0 ? (val>=threshold?255:0) : val
    }
  }

  return dst
} // sobelD5

Algo.bwxSobel = function(src, dst) {
  if(!dst || dst.width!==src.width || dst.height!==src.height) {
    dst = new Uint8Array(src.width*src.height)
    dst.width = src.width
    dst.height = src.height
  }

  const width = src.width
  const height = src.height
  const width1 = width-1
  const height1 = height-1

  // at the image border just clear pixels
  const last_line_offset = width*(height-1)
  for(let x=0; x<width; x++) {
    dst[x] = 0
    dst[last_line_offset+x] = 0
  }
  let first_col_start = 0
  for(let y=0; y<height; y++) {
    dst[first_col_start] = 0
    dst[first_col_start+width1] = 0
    first_col_start += width
  }

  // calculate 3x3 Sobel for all other pixels
  let offset = width+1
  for(let y=1; y<height1; y++) {
    for(let x=1; x<width1; x++) {
      const match = src[offset]===255 && (src[offset+1]===0 || src[offset+width]===0)
      dst[offset] = dst[offset] || match ? 255 : 0
      // dilate surrounding pixels
      dst[offset+width] = dst[offset+width] || match ? 255 : 0
      // dst[offset-width] = dst[offset-width] || match ? 255 : 0
      // dst[offset-1] = dst[offset-1] || match ? 255 : 0
      dst[offset+1] = dst[offset+1] || match ? 255 : 0
      offset++
    }
    offset+=2
  }

  return dst
} // bwxSobel

Algo.laplacian = function(src, dst, threshold=0) {
  if(!dst || dst.width!==src.width || dst.height!==src.height) {
    dst = new Uint8Array(src.width*src.height)
    dst.width = src.width
    dst.height = src.height
  }

  const width = src.width
  const height = src.height
  const width1 = width-1
  const height1 = height-1

  // at the image border just clear pixels
  const last_line_offset = width*(height-1)
  for(let x=0; x<width; x++) {
    dst[x] = 0
    dst[last_line_offset+x] = 0
  }
  let first_col_start = 0
  for(let y=0; y<height; y++) {
    dst[first_col_start] = 0
    dst[first_col_start+width1] = 0
    first_col_start += width
  }

  // calculate 3x3 Laplacian for all other pixels
  let offset = width+1
  for(let y=1; y<height1; y++) {
    for(let x=1; x<width1; x++) {
      let g = src[offset-width] + src[offset-1] + src[offset+1] + src[offset+width] - 4*src[offset]
      g = g<0 ? 0 : g>255 ? 255 : g
      dst[offset] = threshold>0 ? (g >= threshold ? 255 : 0) : g
      offset++
    }
    offset+=2
  }

  return dst
} // laplacian

Algo.findClustersBoolean = function(src, min_size, max_size) {
  const width = src.width
  const height = src.height

  const visited = new Uint8Array(src.width * src.height)
  const clusters = []

  const dx = [-1, 1, 0, 0]
  const dy = [0, 0, -1, 1]

  let index = 0
  for(let y=0; y<height; y++) {
    for (let x=0; x<width; x++, index++) {
      if(!src[index] || visited[index]) continue
      const points = []
      const stack = [[x, y]]
      let xmin = x
      let xmax = x
      let ymin = y
      let ymax = y
      let xsum = 0
      let ysum = 0

      while(stack.length > 0) {
        const [x, y] = stack.pop()
        const idx = y * width + x
        if(visited[idx] || !src[idx]) continue
        visited[idx] = 1
        points.push([x, y])
        xsum+=x
        ysum+=y
        if(x<xmin) xmin=x
        if(x>xmax) xmax=x
        if(y<ymin) ymin=y
        if(y>ymax) ymax=y
        for(let k=0; k<4; k++) {
          const newX = x + dx[k]
          const newY = y + dy[k]
          if(newX>=0 && newX<width && newY>=0 && newY<height) { stack.push([newX, newY]) }
        }
      }

      const length = points.length
      if(min_size>0 && length<min_size) continue
      if(max_size>0 && length>max_size) continue
      const xavg = xsum / length
      const yavg = ysum / length
      const c_width = xmax-xmin+1
      const c_height = ymax-ymin+1
      let deg = Math.atan2(c_width, c_height)*Algo.raddeg
      if(deg<0) deg+=360
      const cluster = {
        length: points.length,
        xmin,
        xmax,
        ymin,
        ymax,
        xavg,
        yavg,
        width: c_width,
        height: c_height,
        points,
        src_width: width,
        src_height: height,
        theta: deg,
      }
      clusters.push(cluster)
    }
  }

  return clusters
} // findClustersBoolean

Algo.findClusters = function(src, min_size, max_size, matchValue) {
  if(matchValue===null || matchValue===undefined) return Algo.findClustersBoolean(src, min_size, max_size)

  const width = src.width
  const height = src.height

  const visited = new Uint8Array(src.width * src.height)
  const clusters = []

  const dx = [-1, 1, 0, 0]
  const dy = [0, 0, -1, 1]

  let index = 0
  for(let y=0; y<height; y++) {
    for (let x=0; x<width; x++, index++) {
      if(src[index]!==matchValue || visited[index]) continue
      const points = []
      const stack = [[x, y]]
      let xmin = x
      let xmax = x
      let ymin = y
      let ymax = y
      let xsum = 0
      let ysum = 0

      while(stack.length > 0) {
        const [x, y] = stack.pop()
        const idx = y * width + x
        if(visited[idx] || src[idx]!==matchValue) continue
        visited[idx] = 1
        points.push([x, y])
        xsum+=x
        ysum+=y
        if(x<xmin) xmin=x
        if(x>xmax) xmax=x
        if(y<ymin) ymin=y
        if(y>ymax) ymax=y
        for(let k=0; k<4; k++) {
          const newX = x + dx[k]
          const newY = y + dy[k]
          if(newX>=0 && newX<width && newY>=0 && newY<height) { stack.push([newX, newY]) }
        }
      }

      const length = points.length
      if(min_size>0 && length<min_size) continue
      if(max_size>0 && length>max_size) continue
      const xavg = xsum / length
      const yavg = ysum / length
      const c_width = xmax-xmin+1
      const c_height = ymax-ymin+1
      const cluster = {
        length: points.length,
        xmin,
        xmax,
        ymin,
        ymax,
        xavg,
        yavg,
        width: c_width,
        height: c_height,
        points,
        src_width: width,
        src_height: height,
      }
      clusters.push(cluster)
    }
  }

  return clusters
} // findClustersBoolean

Algo.findCluster = function(src, x, y, max_size=Infinity, visited, matchValue) {
  const width = src.width
  const height = src.height
  const boolTest = isNaN(matchValue)

  if(!visited) visited = new Uint8Array(src.width * src.height)

  const dx = [-1, 1, 0, 0]
  const dy = [0, 0, -1, 1]

  const points = []
  const stack = [[x, y]]
  let xmin = x
  let xmax = x
  let ymin = y
  let ymax = y
  let xsum = 0
  let ysum = 0

  while(stack.length > 0) {
    const [x, y] = stack.pop()
    const idx = y * width + x
    if(visited[idx] || (boolTest ? !src[idx] : src[idx]!==matchValue)) continue
    visited[idx] = 1
    points.push([x, y])
    if(points.length>max_size) return // too big cluster
    xsum+=x
    ysum+=y
    if(x<xmin) xmin=x
    if(x>xmax) xmax=x
    if(y<ymin) ymin=y
    if(y>ymax) ymax=y
    for(let k=0; k<4; k++) {
      const newX = x + dx[k]
      const newY = y + dy[k]
      if(newX>=0 && newX<width && newY>=0 && newY<height) { stack.push([newX, newY]) }
    }
  }

  const length = points.length
  if(length===0) return
  const xavg = xsum / length
  const yavg = ysum / length
  const c_width = xmax-xmin+1
  const c_height = ymax-ymin+1
  const cluster = { length: points.length, xmin, xmax, ymin, ymax, xavg, yavg, width: c_width, height: c_height, points }
  return cluster
} // findCluster

Algo.drawClusters = function(clusters, dst) {
  if(!dst || !dst.width || !dst.height) return

  const width = dst.width
  const height = dst.height

  dst.fill(255)

  for(const cluster of clusters) {
    for(const [x, y] of cluster.points) {
      if(x<width && y<height) dst[y*width+x] = 0
    }

    for(let x=cluster.xmin; x<=cluster.xmax; x++) {
      dst[cluster.ymin*width+x] = 0
      dst[cluster.ymax*width+x] = 0
      dst[Math.round(cluster.yavg)*width+x] = 0
    }
    for(let y=cluster.ymin; y<=cluster.ymax; y++) {
      dst[y*width+cluster.xmin] = 0
      dst[y*width+cluster.xmax] = 0
      dst[y*width+Math.round(cluster.xavg)] = 0
    }
  }

  return dst
} // drawClusters

Algo.houghTables = function(angle_count) {
  const tables = Algo.houghTablesCache?.[angle_count]
  if(tables) return tables
  if(!Algo.houghTablesCache) Algo.houghTablesCache = {}
  const cos = new Array(angle_count)
  const sin = new Array(angle_count)
  const thetaDiff = Math.PI / angle_count
  for(let idx=0, theta=0; idx<angle_count; idx++, theta+=thetaDiff) {
    cos[idx] = Math.cos(theta)
    sin[idx] = Math.sin(theta)
  }
  const table = Algo.houghTablesCache[angle_count] = { sin, cos }
  return table
} // houghTables

Algo.houghToEuclid = function(r, theta, xmin, xmax, ymin, ymax, round) {
  theta *= Math.PI/180
  const sin = Math.sin(theta)
  const cos = Math.cos(theta)
  const points = []

  // Intersection with xmin
  const yForXmin = (r - xmin * cos) / sin
  if(yForXmin >= ymin && yForXmin <= ymax) points.push({ x: xmin, y: yForXmin })

  // Intersection with xmax
  const yForXmax = (r - xmax * cos) / sin
  if(yForXmax >= ymin && yForXmax <= ymax) points.push({ x: xmax, y: yForXmax })

  // Intersection with ymin
  const xForYmin = (r - ymin * sin) / cos
  if(xForYmin >= xmin && xForYmin <= xmax) points.push({ x: xForYmin, y: ymin })

  // Intersection with ymax
  const xForYmax = (r - ymax * sin) / cos
  if(xForYmax >= xmin && xForYmax <= xmax) points.push({ x: xForYmax, y: ymax })

  // If at least two intersection points are found, return them
  if(points.length >= 2) {
    let x1 = points[0].x
    let y1 = points[0].y
    let x2 = points[1].x
    let y2 = points[1].y
    if(round) {
      [x1, y1, x2, y2] = [x1, y1, x2, y2].map(Math.round)
    }
    return { x1, y1, x2, y2 }
  }

  return {}
} // houghToEuclid

Algo.euclidToPolar = function(x1, y1, x2, y2) {
  const rad = Math.atan2(y2 - y1, x2 - x1)
  let deg = rad * Algo.raddeg
  if(deg<0) deg+=360
  const midX = (x1 + x2) / 2
  const midY = (y1 + y2) / 2
  const rho = midX * Math.cos(rad) + midY * Math.sin(rad)
  return [rho, deg]
} // euclidToPolar

Algo.euclidToHough = function(x1, y1, x2, y2) {
  const rad = Math.atan2(y2 - y1, x2 - x1) + Math.PI/2
  let deg = rad * Algo.raddeg
  if(deg<0) deg+=360
  deg = deg % 360
  const midX = (x1 + x2) / 2
  const midY = (y1 + y2) / 2
  const rho = midX * Math.cos(rad) + midY * Math.sin(rad)
  return [rho, deg]
} // euclidToHough

Algo.houghOnMatrix = function(src, angles, min_match, angle_min=0, angle_max=179.999) {
  const { sin, cos } = Algo.houghTables(angles)
  const acc = new Array(angles)

  // construct hough matrix
  const { width, height } = src
  const angle_idx_min = angle_min * angles / 180
  const angle_idx_max = angle_max * angles / 180
  for(let y=0, i=0; y<height; y++) {
    for(let x=0; x<width; x++, i++) {
      if(src[i]) {
        for(let angleIdx=angle_idx_min; angleIdx<=angle_idx_max; angleIdx++) {
          let rho = x * cos[angleIdx] + y * sin[angleIdx]
          rho = Math.round(rho)
          if(!acc[angleIdx]) acc[angleIdx] = {}
          if(!acc[angleIdx][rho]) acc[angleIdx][rho] = 1
          else acc[angleIdx][rho]++
        }
      }
    }
  }

  // find lines with enough points
  const lines = []
  for(let angleIdx=angle_idx_min; angleIdx<=angle_idx_max; angleIdx++) {
    for(let rho in acc[angleIdx]) {
      const matches = acc[angleIdx][rho]
      if(matches>min_match) {
        rho = parseFloat(rho)
        const r = rho
        const theta = angleIdx*180/angles
        lines.push({ r, theta, matches })
      }
    }
  }

  return lines
} // houghOnMatrix

Algo.hough = function(black_cluster, angles, min_match) {
  const { sin, cos } = Algo.houghTables(angles)
  const acc = new Array(angles)

  // construct hough matrix
  for(const [x, y] of black_cluster.points) {
    for(let angleIdx=0; angleIdx<angles; angleIdx++) {
      let rho = x * cos[angleIdx] + y * sin[angleIdx]
      rho = Math.round(rho)
      if(!acc[angleIdx]) acc[angleIdx] = {}
      if(!acc[angleIdx][rho]) acc[angleIdx][rho] = 1
      else acc[angleIdx][rho]++
    }
  }

  // find lines with enough points
  const lines = []
  for(let angleIdx=0; angleIdx<angles; angleIdx++) {
    for(let rho in acc[angleIdx]) {
      const matches = acc[angleIdx][rho]
      if(matches>min_match) {
        rho = parseFloat(rho)
        const r = rho
        const theta = angleIdx*180/angles
        lines.push({ r, theta, matches })
      }
    }
  }

  return lines
} // hough

Algo.lineMatches = function(black_cluster, line) {
  const { width, height } = black_cluster
  let { x1, y1, x2, y2 } = line
  if(isNaN(x1) && !isNaN(line.theta)) {
    ({ x1, y1, x2, y2 } = Algo.houghToEuclid(line.r, line.theta, 0, width, 0, height))
  }

  let points = Algo.linePoints(x1, y1, x2, y2)
  points = points.filter(([x, y])=>black_cluster[y*width+x])

  return points
} // lineMatches

Algo.cluster = function(lines, neighbors, max_distance=2, min_count=1) {
  function dbscan(lines, eps, minPts) {
    const clusters = []
    const visited = new Set()
    const noise = new Set()

    for(const line of lines) {
      if(visited.has(line)) continue
      visited.add(line)
      const lineNeighbors = neighbors(line, lines, eps)
      if(lineNeighbors.length < minPts) {
        noise.add(line)
        continue
      }
      const cluster = []
      clusters.push(cluster)
      for(const line of lineNeighbors) {
        if(!visited.has(line)) {
          visited.add(line)
          const furtherNeighbors = neighbors(line, lines, eps)
          if(furtherNeighbors.length >= minPts) {
            lineNeighbors.push(...furtherNeighbors.filter(p => !lineNeighbors.includes(p)))
          }
        }
        if(!clusters.some(c => c.includes(line))) {
          cluster.push(line)
        }
      }
    }

    return { clusters, noise: Array.from(noise) }
  }

  // find clusters of lines
  const { clusters } = dbscan(lines, max_distance, min_count)
  return clusters
} // cluster

Algo.clusterHoughLines = function(lines, max_distance=2, min_count=1) {
  function euclideanDistance(line1, line2) {
    return Math.sqrt((line1.r - line2.r)**2 + (line1.theta - line2.theta)**2)
  }
  function neighbors(line, lines, max_distance) {
    const fcn = typeof max_distance==='function' ? max_distance.bind(null, line) : (line2) => euclideanDistance(line, line2) < max_distance
    return lines.filter(fcn)
  }
  function dbscan(lines, max_distance, minPts) {
    const clusters = []
    const visited = new Set()
    const noise = new Set()

    for(const line of lines) {
      if(visited.has(line)) continue
      visited.add(line)
      const lineNeighbors = neighbors(line, lines, max_distance)
      if(lineNeighbors.length < minPts) {
        noise.add(line)
        continue
      }
      const cluster = []
      clusters.push(cluster)
      for(const line of lineNeighbors) {
        if(!visited.has(line)) {
          visited.add(line)
          const furtherNeighbors = neighbors(line, lines, max_distance)
          if(furtherNeighbors.length >= minPts) {
            lineNeighbors.push(...furtherNeighbors.filter(p => !lineNeighbors.includes(p)))
          }
        }
        if(!clusters.some(c => c.includes(line))) {
          cluster.push(line)
        }
      }
    }

    return { clusters, noise: Array.from(noise) }
  }

  // find clusters of lines
  const { clusters } = dbscan(lines, max_distance, min_count)
  const out = []

  for(const cluster of clusters) {
    let sum_r = 0
    let sum_theta_x = 0
    let sum_theta_y = 0
    let matches = 0
    for(const line of cluster) {
      sum_r+=line.r
      const rad = (line.theta * Math.PI) / 180
      sum_theta_x += Math.cos(rad)
      sum_theta_y += Math.sin(rad)
      matches+=line.matches
    }
    const r = sum_r / cluster.length
    const avg_theta_x = sum_theta_x / cluster.length
    const avg_theta_y = sum_theta_y / cluster.length
    const avg_rad = Math.atan2(avg_theta_y, avg_theta_x)
    let theta = (avg_rad * 180) / Math.PI
    if(theta < 0) theta += 360
    theta = Algo.roundBy(theta, 1)
    out.push({ r, theta, matches, total: cluster[0].total, lines: cluster })
    // console.log('  CLUSTER: ',{r,theta}, cluster.map(line=>`${line.r},${line.theta}`).join('  '))
  }

  return out
} // clusterHoughLines

Algo.houghSegments = function(src, lines, minSegmentLength=2) {
  const { width, height } = src
  const width1 = width-1
  const height1 = height-1
  const segments = []
  for(const line of lines) {
    const { r, theta } = line
    const rad = theta * Math.PI/180
    const sin = Math.sin(rad)
    const cos = Math.cos(rad)
    if(theta<45 || theta>=135) {
      // more like vertical line, traverse using Y axis
      let x = r / cos
      const dx = -sin / cos
      let x1 = -1
      let y1 = -1
      let segmentLength = 0
      for(let y=0; y<height; y++, x+=dx) {
        if(x<0 || x>=width) continue
        const x0 = Math.round(x)
        const pos = y*width + x0
        const match = src[pos] || (x0>0 && src[pos-1]) || (x0<width1 && src[pos+1])
        if(match) {
          if(x1 === -1) {
            // first segment point
            x1 = x
            y1 = y
          }
          segmentLength++
        }else{
          if(segmentLength >= minSegmentLength) {
            // first non-segment point
            const y2 = y-1
            const x2 = x-dx
            segments.push({ r, theta, x1: Math.round(x1), y1, x2: Math.round(x2), y2 })
          }
          segmentLength = 0
          x1 = -1
        }
      }
    }else{
      // more like horizontal line, traverse using X axis
      let y = r / sin
      const dy = -cos / sin
      let x1 = -1
      let y1 = -1
      let segmentLength = 0
      for(let x=0; x<width; x++, y+=dy) {
        if(y<0 || y>=height) continue
        const y0 = Math.round(y)
        const pos = y0*width + x
        const match = src[pos] || (y0>0 && src[pos-width]) || (y0<height1 && src[pos+width])
        if(match) {
          if(x1 === -1) {
            // first segment point
            x1 = x
            y1 = y
          }
          segmentLength++
        }else{
          if(segmentLength >= minSegmentLength) {
            // first non-segment point
            const x2 = x-1
            const y2 = y-dy
            segments.push({ r, theta, x1, y1: Math.round(y1), x2, y2: Math.round(y2) })
          }
          segmentLength = 0
          x1 = -1
        }
      }
    }
  }

  return segments
} // houghSegments

Algo.linePoints = function(x1, y1, x2, y2) {
  const dx = Math.abs(x1-x2)
  const dy = Math.abs(y1-y2)
  const points = []
  if(dx<1 && dy<1) {
    // a single point
    const x = Math.round(x1)
    const y = Math.round(y1)
    points.push([x, y])
  }else if(dx>dy) {
    // horizontal line
    if(x1>x2) {
      [x1, y1, x2, y2] = [x2, y2, x1, y1]
    }
    const d = (y1-y2)/(x1-x2)
    const x1_rounded = Math.round(x1)
    y1 = y1 + (x1_rounded-x1)*d
    for(let x=x1_rounded, y=y1; x<=x2; x++, y+=d) {
      const y_rounded = Math.round(y)
      points.push([x, y_rounded])
    }
  }else {
    // vertical line
    if(y1>y2) {
      [x1, y1, x2, y2] = [x2, y2, x1, y1]
    }
    const d = (x1-x2)/(y1-y2)
    const y1_rounded = Math.round(y1)
    x1 = x1 + (y1_rounded-y1)*d
    for(let x=x1, y=y1_rounded; y<=y2; y++, x+=d) {
      const x_rounded = Math.round(x)
      points.push([x_rounded, y])
    }
  }
  return points
} // linePoints

Algo.linearRegression = function(points) {
  let sum_x = 0
  let sum_y = 0
  let sum_xx = 0
  let sum_xy = 0
  let sum_yy = 0
  const count = points.length
  for(let i=0; i<count; i++) {
    const x = points[i][0]
    const y = points[i][1]
    sum_x += x
    sum_y += y
    sum_xx += x*x
    sum_xy += x*y
    sum_yy += y * y
  }

  const mx = (count * sum_xy - sum_x * sum_y) / (count * sum_xx - sum_x * sum_x)
  const my = (count * sum_xy - sum_x * sum_y) / (count * sum_yy - sum_y * sum_y)
  const bx = (sum_y - mx * sum_x) / count
  const by = (sum_x - my * sum_y) / count

  if(isNaN(my) || Math.abs(mx) < Math.abs(my) || (mx===0 && my===0 && (count*sum_xx - sum_x*sum_x) > (count*sum_yy - sum_y*sum_y))) {
    // closer to horizontal
    let rad = Math.atan(-1/mx)
    while(rad < 0) rad += 2*Math.PI
    while(rad >= Math.PI) rad -= Math.PI
    let theta = rad*180/Math.PI
    if(theta<0) theta+=360
    const rho = bx * Math.sin(rad)
    return { mx, bx, rho, rad, theta }
  }else{
    // closer to vertical
    let rad = Math.atan(-my)
    while(rad < 0) rad += 2*Math.PI
    while(rad >= Math.PI) rad -= Math.PI
    let theta = rad*180/Math.PI
    if(theta<0) theta+=360
    const rho = by * Math.cos(rad)
    return { my, by, rho, rad, theta }
  }
} // linearRegression

Algo.clusterIsCross = function(cluster) {
  const { length, xmin, xmax, ymin, ymax, width, height, points, src_width, src_height } = cluster

  // cross pattern fills just small part of bounding box
  if(length > 0.1 * cluster.width * cluster.height) return false

  // construct matrix of bounding box
  // with removed center of bounding box to separate 4 parts of cross
  const src = new Uint8Array(width*height).fill(0)
  src.width = width
  src.height = height
  const mid_portion = 1/4
  const midx1 = Math.round(xmin + (1-mid_portion)/2*width)
  const midx2 = Math.round(midx1 + mid_portion*width)
  const midy1 = Math.round(ymin + (1-mid_portion)/2*height)
  const midy2 = Math.round(midy1 + mid_portion*height)
  for(const [x, y] of points) {
    if(x<midx1 || x>midx2 || y<midy1 || y>midy2) {
      src[(y-ymin)*width+(x-xmin)] = 255
    }
  }

  // find clusters in bounding box
  // as we removed the center of bounding box,
  // these should be 4 separate lines as two perpendicular pairs of colinear lines
  const lines = cluster.lines = Algo.findClusters(src, 5)
  if(lines.length!==4) return false

  // interpolate points for each line
  // and obtain polar coordinates
  for(const line of lines) {
    line.points = line.points.map(([x, y])=>([x+xmin, y+ymin]))
    line.xmin += xmin
    line.xavg += xmin
    line.xmax += xmin
    line.ymin += ymin
    line.yavg += ymin
    line.ymax += ymin
    const { rho, theta } = Algo.linearRegression(line.points)
    line.rho = rho
    line.theta = theta
  }

  // group colinear lines
  const max_rho = Math.sqrt(src_width*src_width + src_height*src_height)
  const norm_rho = 360 / max_rho // normalise rho to same "resolution as angles"
  const groups = []
  // eslint-disable-next-line no-labels
  LINE: for(const line of lines) {
    const { rho, theta: theta1 } = line
    const rho1 = rho*norm_rho
    for(const g of groups) {
      for(const line2 of g) {
        const { rho, theta: theta2 } = line2
        const rho2 = rho*norm_rho
        const diff = Math.sqrt((rho1-rho2)**2 + Algo.degreeDiff(theta1, theta2)**2)
        if(diff<5) {
          g.push(line)
          // eslint-disable-next-line no-labels
          continue LINE
        }
      }
    }
    groups.push([line])
  }

  // for(let g of groups){
  //   console.log("GROUP:", g.map(({rho,theta})=>rho+'/'+theta+'°').join(', '))
  //   console.log('  LINES:', g.map(({xmin,xmax,ymin,ymax})=>`${xmin}-${xmax}/${ymin}-${ymax}`).join(', '))
  // }

  if(groups.length!==2) return console.log('NUMBER OF GROUPS !=2: ', groups.length) // no two groups
  if(groups[0].length!==2) return console.log('GROUPS NOT 2+2: ', groups[0].length, groups[1].length)// first group has not two lines; we don't check second group as all together are 4

  // calculate more precise line characteristics from both ends of lines forming corss
  const cross_lines = []
  for(let i=0; i<=1; i++) {
    const a = groups[i][0]
    const b = groups[i][1]
    const points = a.points.concat(b.points)
    const regression = Algo.linearRegression(points)
    const { rho, theta } = regression
    const xmin = Math.min(a.xmin, b.xmin)
    const xmax = Math.max(a.xmax, b.xmax)
    const ymin = Math.min(a.ymin, b.ymin)
    const ymax = Math.max(a.ymax, b.ymax)
    const horizontal = (xmax-xmin) >= (ymax-ymin)
    let { x1, y1, x2, y2 } = Algo.houghToEuclid(rho, theta, horizontal?xmin:0, horizontal?xmax:src_width, horizontal?0:ymin, horizontal?src_height:ymax)
    x1 = Math.round(x1)
    y1 = Math.round(y1)
    x2 = Math.round(x2)
    y2 = Math.round(y2)
    cross_lines.push({ ...regression, xmin, xmax, ymin, ymax, x1, y1, x2, y2, points })
  }

  // find center
  const { rho: rho1, rad: rad1, theta: theta1 } = cross_lines[0]
  const { rho: rho2, rad: rad2, theta: theta2 } = cross_lines[1]
  const det = Math.cos(rad1) * Math.sin(rad2) - Math.sin(rad1) * Math.cos(rad2)
  if(Math.abs(det) < 1e-6) return // they are parallel; should not happen
  const cross_x = cluster.cross_x = Math.round((rho1 * Math.sin(rad2) - rho2 * Math.sin(rad1)) / det)
  const cross_y = cluster.cross_y = Math.round((rho2 * Math.cos(rad1) - rho1 * Math.cos(rad2)) / det)

  // verify intersection is within cluster boundary
  if(cross_x<xmin || cross_x>xmax || cross_y<ymin || cross_y>ymax) return

  // determine which line is more horizontal and which more vertical
  const diff_from_horizont1 = Algo.degreeDiff(theta1, 90)
  const diff_from_horizont2 = Algo.degreeDiff(theta2, 90)
  if(diff_from_horizont1<diff_from_horizont2) {
    cluster.horizontal = cross_lines[0]
    cluster.vertical = cross_lines[1]
  }else{
    cluster.vertical = cross_lines[0]
    cluster.horizontal = cross_lines[1]
  }

  cluster.angle = Algo.degreeAvg(theta1, theta2) - 45
  return true
} // clusterIsCross

Algo.clusterIsCircle = function(cluster) {
  let { length, xavg, yavg, width, height, points } = cluster
  xavg = Math.round(xavg)
  yavg = Math.round(yavg)

  if(width<5 || height<5) return false

  // ideal circle fills 3.14/4=78% of square
  const filled = length / (width * height) *100
  if(filled < 60 || filled > 85) return false

  // calculate maximum radius of cluster
  const distancesSq = []
  let radiusSq = -Infinity
  for(const [x, y] of points) {
    const distanceSq = (x-xavg)**2 + (y-yavg)**2
    if(distanceSq>radiusSq) radiusSq = distanceSq
    distancesSq.push(distanceSq)
  }
  const radius = Math.floor(Math.sqrt(radiusSq))
  if(radius<10) return false
  cluster.circle = {
    x: xavg,
    y: yavg,
    radius,
  }
  return true
} // clusterIsCircle

Algo.boundingBox = function(points, rounded=true) {
  if(points.length===0) return {}

  // Calculate centroid and bounds of the points
  let xmin = Infinity
  let ymin = Infinity
  let xmax = -Infinity
  let ymax = -Infinity
  let xsum = 0
  let ysum = 0
  for(const [x, y] of points) {
    xsum += x
    ysum += y
    if(xmin>x) xmin=x
    if(xmax<x) xmax=x
    if(ymin>y) ymin=y
    if(ymax<y) ymax=y
  }
  let xavg = xsum / points.length
  let yavg = ysum / points.length
  let xmid = (xmax + xmin) / 2
  let ymid = (ymax + ymin) / 2
  if(rounded) {
    xavg = Math.round(xavg)
    yavg = Math.round(yavg)
    xmid = Math.round(xmid)
    ymid = Math.round(ymid)
  }
  return { xmin, xavg, xmid, xmax, ymin, yavg, ymid, ymax }
} // boundingBox

Algo.boundingEllipse = function(points, threshold=2, rounded=true, outliers) {
  if(points.length===0) return {}
  const { xmin, xavg, xmid, xmax, ymin, yavg, ymid, ymax } = Algo.boundingBox(points, rounded)

  let xsum2 = 0
  let ysum2 = 0
  for(const [x, y] of points) {
    xsum2 += (x - xavg)**2
    ysum2 += (y - yavg)**2
  }

  let a = Math.sqrt(xsum2 / points.length)
  let b = Math.sqrt(ysum2 / points.length)

  if(threshold>0) {
    const inliers = []
    const outliers = []
    for(const [x, y] of points) {
      const xscore = Math.abs((x - xavg) / a)
      const yscore = Math.abs((y - yavg) / b)
      if(xscore<=threshold && yscore<=threshold) {
        inliers.push([x, y])
      }else {
        outliers.push([x, y])
      }
    }
    return Algo.boundingEllipse(inliers, 0, rounded, outliers)
  }else{
    const inliers = points
    outliers = outliers || []
    if(rounded) {
      a = Math.round(a)
      b = Math.round(b)
    }
    return { xmin, xavg, xmid, xmax, ymin, yavg, ymid, ymax, a, b, inliers, outliers }
  }
} // boundingEllipse

Algo.rotateAndTranslate = function(x, y, cx, cy, dx, dy, phi) {
  // Apply translation
  const xPrime = x + dx
  const yPrime = y + dy

  // Translate point so that center of rotation becomes the origin
  const xTranslated = xPrime - cx
  const yTranslated = yPrime - cy

  // Rotate the translated point
  const xRotated = xTranslated * Math.cos(phi) - yTranslated * Math.sin(phi)
  const yRotated = xTranslated * Math.sin(phi) + yTranslated * Math.cos(phi)

  // Translate the point back
  const xFinal = xRotated + cx
  const yFinal = yRotated + cy

  return [xFinal, yFinal]
} // rotateAndTranslate

Algo.circlePoints = function(cx, cy, radius, count) {
  const points = []
  // cx /= 2
  // cy /= 2
  for(let i = 0; i<count; i++) {
    const theta = 2 * Math.PI * i / count
    const x = cx + radius * Math.cos(theta)
    const y = cy + radius * Math.sin(theta)
    points.push([x, y])
  }
  return points
} // circlePoints

Algo.distanceFromLine = function(x, y, line) {
  if(!isNaN(line.x1)) {
    const { x1, y1, x2, y2 } = line
    const numerator = Math.abs((y2 - y1) * x - (x2 - x1) * y + x2 * y1 - y2 * x1)
    const denominator = Math.sqrt((y2 - y1) ** 2 + (x2 - x1) ** 2)
    const distance = numerator / denominator
    return distance
  }else if(!isNaN(line.theta)) {
    const rho = !isNaN(line.rho) ? line.rho : line.r
    const theta = line.theta*Algo.degrad
    const distance = Math.abs(x * Math.cos(theta) + y * Math.sin(theta) - rho)
    return distance
  }else {
    return Infinity
  }
} // distanceFromLine

Algo.linesAngle = function(line1, line2) {
  if(Array.isArray(line1)) {
    if(Array.isArray(line1[0])) {
      line1 = {
        x1: line1[0][0],
        y1: line1[0][1],
        x2: line1[1][0],
        y2: line1[1][1],
      }
    }else {
      line1 = {
        x1: line1[0],
        x2: line1[1],
        y1: line1[2],
        y2: line1[3],
      }
    }
  }
  if(Array.isArray(line2)) {
    if(Array.isArray(line2[0])) {
      line2 = {
        x1: line2[0][0],
        y1: line2[0][1],
        x2: line2[1][0],
        y2: line2[1][1],
      }
    }else {
      line2 = {
        x1: line2[0],
        x2: line2[1],
        y1: line2[2],
        y2: line2[3],
      }
    }
  }
  const dx1 = line1.x2 - line1.x1
  const dy1 = line1.y2 - line1.y1
  const dx2 = line2.x2 - line2.x1
  const dy2 = line2.y2 - line2.y1
  const prod = dx1 * dx2 + dy1 * dy2
  const mag1 = Math.sqrt(dx1 ** 2 + dy1 ** 2)
  const mag2 = Math.sqrt(dx2 ** 2 + dy2 ** 2)
  const angle = Math.acos(prod / (mag1 * mag2))
  return angle * 180 / Math.PI
} // linesAngle

Algo.triAngle = function(x0, y0, x1, y1, x2, y2) {
  const angle1 = Math.atan2(y1 - y0, x1 - x0)
  const angle2 = Math.atan2(y2 - y0, x2 - x0)
  let angleDifference = angle2 - angle1
  angleDifference = (angleDifference + Math.PI) % (2 * Math.PI) - Math.PI
  return Math.abs(angleDifference * Algo.raddeg)
} // triAngle

Algo.intersection = function (line1, line2) {
  let x1, y1, x2, y2
  let x3, y3, x4, y4
  if(Array.isArray(line1)) {
    [x1, y1, x2, y2] = line1
  }else{
    ({ x1, y1, x2, y2 } = line1)
  }
  if(Array.isArray(line2)) {
    [x3, y3, x4, y4] = line2
  }else{
    const { x1, y1, x2, y2 } = line2;
    [x3, y3, x4, y4] = [x1, y1, x2, y2]
  }

  const a1 = y2 - y1
  const b1 = x1 - x2
  const c1 = a1 * x1 + b1 * y1

  const a2 = y4 - y3
  const b2 = x3 - x4
  const c2 = a2 * x3 + b2 * y3

  const determinant = a1 * b2 - a2 * b1
  if(determinant === 0) return []

  const x = (b2 * c1 - b1 * c2) / determinant
  const y = (a1 * c2 - a2 * c1) / determinant

  return [x, y]
} // intersection

Algo.clipLineInRect = function(p1, p2, xmin, xmax, ymin, ymax) {
  const [x1, y1] = p1
  const [x2, y2] = p2
  const dx = x2 - x1
  const dy = y2 - y1
  let t0 = 0; let t1 = 1

  const clip = (p, q) => {
    if (p === 0) return q >= 0
    const r = q / p
    if (p < 0) { if (r > t1) return false; if (r > t0) t0 = r } else { if (r < t0) return false; if (r < t1) t1 = r }
    return true
  }

  if (
    clip(-dx, x1 - xmin) &&
    clip(dx, xmax - x1) &&
    clip(-dy, y1 - ymin) &&
    clip(dy, ymax - y1)
  ) {
    const ax = x1 + t0 * dx; const ay = y1 + t0 * dy
    const bx = x1 + t1 * dx; const by = y1 + t1 * dy
    return [[ax, ay], [bx, by]]
  }
  return []
} // clipLineInRect

Algo.circleLineIntersection = function(cx, cy, r, x1, y1) {
  // special case when line begins at center of circle
  const dx = x1 - cx
  const dy = y1 - cy
  const length = Math.hypot(dx, dy)
  const scale = r / length
  const ix = cx + dx * scale
  const iy = cy + dy * scale
  return [ix, iy]
} // circleLineIntersection

Algo.circleLineIntersections = function(cx, cy, r, x1, y1, x2, y2) {
  const dx = x2 - x1
  const dy = y2 - y1

  const fx = x1 - cx
  const fy = y1 - cy

  const a = dx * dx + dy * dy
  const b = 2 * (fx * dx + fy * dy)
  const c = fx * fx + fy * fy - r * r

  const discriminant = b * b - 4 * a * c

  if(discriminant < 0) {
    return [] // no intersection
  }

  const intersections = []
  const sqrtD = Math.sqrt(discriminant)

  for(const sign of [-1, 1]) {
    const t = (-b + sign * sqrtD) / (2 * a)
    if(t >= 0 && t <= 1) {
      intersections.push([
        x1 + dx * t,
        y1 + dy * t,
      ])
    }
  }

  return intersections
} // circleLineIntersections

Algo.intersectionsAverage = function(lines) {
  // find intersections of each pair if lines
  const intersections = []
  for(let i=0; i<lines.length; i++) {
    for(let j = i+1; j<lines.length; j++) {
      const pt = Algo.intersection(lines[i], lines[j])
      if(pt) intersections.push(pt)
    }
  }

  if(intersections.length === 0) return [0, 0]

  // average intyersection – simple estimate
  const sum = intersections.reduce((acc, [x, y]) => [acc[0] + x, acc[1] + y], [0, 0])
  return [
    sum[0] / intersections.length,
    sum[1] / intersections.length,
    intersections,
  ]
} // intersectionsAverage

Algo.clipPolygon = function(polygon, clipper) {
  let cp1, cp2, s, e
  const inside = function(p) {
    return (
      (cp2[0] - cp1[0]) * (p[1] - cp1[1]) > (cp2[1] - cp1[1]) * (p[0] - cp1[0])
    )
  }
  const intersection = function() {
    const dc = [cp1[0] - cp2[0], cp1[1] - cp2[1]]
    const dp = [s[0] - e[0], s[1] - e[1]]
    const n1 = cp1[0] * cp2[1] - cp1[1] * cp2[0]
    const n2 = s[0] * e[1] - s[1] * e[0]
    const n3 = 1.0 / (dc[0] * dp[1] - dc[1] * dp[0])
    return [(n1 * dp[0] - n2 * dc[0]) * n3, (n1 * dp[1] - n2 * dc[1]) * n3]
  }
  let outputList = polygon
  cp1 = clipper[clipper.length - 1]
  for(const j in clipper) {
    cp2 = clipper[j]
    const inputList = outputList
    outputList = []
    s = inputList[inputList.length - 1] //last on the input list
    for(const i in inputList) {
      e = inputList[i]
      if (inside(e)) {
        if (!inside(s)) {
          outputList.push(intersection())
        }
        outputList.push(e)
      } else if (inside(s)) {
        outputList.push(intersection())
      }
      s = e
    }
    cp1 = cp2
  }
  return outputList
} // clipPolygon

Algo.distortPoint = function(x, y, cx, cy, k1, k2, k3) {
  x -= cx
  y -= cy
  const r2 = x*x + y*y
  const r4 = r2 * r2
  const r6 = r4 * r2
  const distortion = 1 + k1*r2 + k2*r4 + k3*r6
  const x2 = cx + x * distortion
  const y2 = cy + y * distortion
  return [x2, y2]
} // distortPoint

Algo.undistortPoint = function(xd, yd, cx, cy, k1, k2, k3, round=true) {
  let x = xd - cx
  let y = yd - cy

  for (let i = 0; i < 5; i++) {
    const r2 = x*x + y*y
    const r4 = r2 * r2
    const r6 = r4 * r2
    const distortion = 1 + k1*r2 + k2*r4 + k3*r6

    x = (xd - cx) / distortion
    y = (yd - cy) / distortion
  }

  if(round) {
    x = Math.round(x)
    y = Math.round(y)
  }

  return [cx + x, cy + y]
} // undistortPoint

Algo.minimizeRandom = function(costFunction, guess, step, iterations) {
  let best = guess.slice()
  let best_cost = costFunction(best)
  for(let i=0; i<iterations; i++) {
    const candidate = best.map((v) => v + (Math.random() - 0.5) * step)
    const cost = costFunction(candidate)
    if(cost < best_cost) {
      best = candidate
      best_cost = cost
    }
  }
  return best
} // minimizeRandom

Algo.multiplyMatrices = function(a, b) {
  const result = []
  const il = a.length
  const jl = b[0].length
  const kl = a[0].length
  let sum
  for(let i=0; i<il; i++) {
    result[i] = []
    for(let j=0; j<jl; j++) {
      sum = 0
      for(let k=0; k<kl; k++) {
        sum += a[i][k] * b[k][j]
      }
      result[i][j] = sum
    }
  }
  return result
} // multiplyMatrices

Algo.multiplyMatrixVector = function(matrix, vector) {
  const result = []
  const il = matrix.length
  const jl = vector.length
  for(let i=0; i<il; i++) {
    let sum = 0
    for(let j=0; j<jl; j++) {
      sum += matrix[i][j] * vector[j]
    }
    result.push(sum)
  }
  return result
} // multiplyMatrixVector

Algo.inverseMatrix = function(matrix) {
  const [a, b, c, d, e, f, g, h, i] = matrix.flat()
  const det = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g)
  if(det === 0) return

  // Calculate the adjugate matrix
  const adjugate = [
    [(e * i - f * h), -(b * i - c * h), (b * f - c * e)],
    [-(d * i - f * g), (a * i - c * g), -(a * f - c * d)],
    [(d * h - e * g), -(a * h - b * g), (a * e - b * d)],
  ]

  const inverse = adjugate.map(row => row.map(val => val / det))
  return inverse
} // inverseMatrix

Algo.rotationMatrix = function(yaw_deg, pitch_deg, roll_deg) {
  // Vehicle Coordinate System XYZ / RPY
  // yaw (Z) is the rotation around the vertical axis (steering left-right)
  // pitch (Y) is the rotation around the lateral axis (nose goes up-down)
  // roll (X) is the rotation around the longitudinal (leaning left-right)

  // Convert angles to radians
  const yaw = yaw_deg * Algo.degrad
  const pitch = pitch_deg * Algo.degrad
  const roll = roll_deg * Algo.degrad

  const Rz = [
    [Math.cos(yaw), -Math.sin(yaw), 0],
    [Math.sin(yaw), Math.cos(yaw), 0],
    [0, 0, 1],
  ]
  const Ry = [
    [Math.cos(pitch), 0, Math.sin(pitch)],
    [0, 1, 0],
    [-Math.sin(pitch), 0, Math.cos(pitch)],
  ]
  const Rx = [
    [1, 0, 0],
    [0, Math.cos(roll), -Math.sin(roll)],
    [0, Math.sin(roll), Math.cos(roll)],
  ]

  // Combined rotation matrix: R = Rz * Ry * Rx
  return Algo.multiplyMatrices(Algo.multiplyMatrices(Rz, Ry), Rx)
} // rotationMatrix

Algo.cameraMatrix = function(h_px, v_px, h_aov, v_aov) {
  // Camera intrinsic parameters
  const f_x = h_px / 2 / Math.tan(h_aov * Algo.degrad / 2)
  const f_y = v_px / 2 / Math.tan(v_aov * Algo.degrad / 2)
  const cameraMatrix = [
    [f_x, 0, h_px / 2],
    [0, f_y, v_px / 2],
    [0, 0, 1],
  ]
  return cameraMatrix
} // cameraMatrix

Algo.camera = function(calibration, obj) {
  if(!calibration) {
    if(obj) {
      delete obj.cameraMatrix
      delete obj.rotationMatrix
    }
    return
  }
  const { h_pixels, v_pixels, h_angle, v_angle, camera_yaw, camera_pitch, camera_roll } = calibration
  const cameraMatrix = Algo.cameraMatrix(h_pixels, v_pixels, h_angle, v_angle)
  const rotationMatrix = Algo.rotationMatrix(camera_yaw, camera_pitch, camera_roll)
  if(obj) {
    obj.cameraMatrix = cameraMatrix
    obj.rotationMatrix = rotationMatrix
  }
  return { cameraMatrix, rotationMatrix }
} // camera

Algo.rig = function(calibration, optics) {
  if(!calibration || typeof calibration !== 'object') return
  const rig = optics && typeof optics === 'object'? { ...calibration, ...optics } : { ...calibration }

  let { h_pixels, v_pixels, h_angle, v_angle } = rig
  h_pixels = parseInt(h_pixels)
  v_pixels = parseInt(v_pixels)
  h_angle = parseFloat(h_angle)
  v_angle = parseFloat(v_angle)
  if(!(h_pixels > 0 && v_pixels > 0 && h_angle > 0 && v_angle > 0)) return
  rig.width = h_pixels
  rig.height = v_pixels
  rig.cameraMatrix = Algo.cameraMatrix(h_pixels, v_pixels, h_angle, v_angle)

  let { camera_yaw, camera_pitch, camera_roll } = rig
  camera_yaw = parseFloat(camera_yaw)
  camera_pitch = parseFloat(camera_pitch)
  camera_roll = parseFloat(camera_roll)
  if(isNaN(camera_yaw) || isNaN(camera_pitch) || isNaN(camera_roll)) return
  rig.rotationMatrix = Algo.rotationMatrix(camera_yaw, camera_pitch, camera_roll)

  rig.project = (point, round) => Algo.project(point, rig.rotationMatrix, rig.cameraMatrix, round)

  let { camera_height } = rig
  camera_height = parseFloat(camera_height)
  if(!isNaN(camera_height)) rig.camera_height = camera_height

  let { camera_side, camera_forward, radar_side, radar_forward } = rig
  camera_side = parseFloat(camera_side)
  camera_forward = parseFloat(camera_forward)
  radar_side = parseFloat(radar_side)
  radar_forward = parseFloat(radar_forward)
  const camera_radar_side = camera_side + radar_side
  const camera_radar_forward = camera_forward - radar_forward
  if(!isNaN(camera_side)) rig.camera_side = camera_side
  if(!isNaN(camera_forward)) rig.camera_forward = camera_forward
  if(!isNaN(radar_side)) rig.radar_side = radar_side
  if(!isNaN(radar_forward)) rig.radar_forward = radar_forward
  if(!isNaN(camera_radar_side)) rig.camera_radar_side = camera_radar_side
  if(!isNaN(camera_radar_forward)) rig.camera_radar_forward = camera_radar_forward

  let { radar_yaw, radar_h_angle } = rig
  radar_yaw = parseFloat(radar_yaw)
  radar_h_angle = parseFloat(radar_h_angle)
  if(!isNaN(radar_yaw)) rig.radar_yaw = radar_yaw
  if(!isNaN(radar_h_angle)) rig.radar_h_angle = radar_h_angle

  return rig
} // rig

Algo.anglesForZoom = function(zooms, zoom, tolerance = 0.02) {
  if(!zooms || typeof zooms!=='object') return
  zoom = parseFloat(zoom)
  if(!(zoom > 0)) return
  tolerance = parseFloat(tolerance)
  if(isNaN(tolerance) || tolerance < 0) return

  const levels = Object.keys(zooms)
    .map(z=>parseFloat(z))
    .filter(z=>!isNaN(z) && !isNaN(zooms[z]?.[0]) && !isNaN(zooms[z]?.[1]))
    .sort((a, b)=>a-b)
  if(levels.length===0) return

  const zooms_by_diff = levels.slice().sort((a, b) => Math.abs(a - zoom) - Math.abs(b - zoom))
  if(Math.abs(zooms_by_diff[0] - zoom) <= tolerance) {
    // practically exact match found
    const z0 = zooms_by_diff[0]
    let [h_angle, v_angle, d_angle, dx, dy, k1, k2, k3] = zooms[z0]
    h_angle = parseFloat(h_angle)
    v_angle = parseFloat(v_angle)
    d_angle = parseFloat(d_angle)
    dx = parseFloat(dx)
    dy = parseFloat(dy)
    k1 = parseFloat(k1)
    k2 = parseFloat(k2)
    k3 = parseFloat(k3)
    const angles = { z0, zoom, h_angle, v_angle }
    if(!isNaN(d_angle)) angles.d_angle = d_angle
    if(!isNaN(dx)) angles.dx = dx
    if(!isNaN(dy)) angles.dy = dy
    if(!isNaN(k1)) angles.k1 = k1
    if(!isNaN(k2)) angles.k2 = k2
    if(!isNaN(k3)) angles.k3 = k3
    return angles
  }

  // no exact match found; find closest smaller and larger zooms
  if(levels.length===1) return // only one zoom is available; cannot interpolate

  let z1 = levels.findLast(f=>f<zoom) // find closest smaller zoom
  let z2 = levels.find(f=>f>zoom) // find closest larger zoom

  if(!z1) {
    // no smaller zoom found; use two smallest for interpolation
    z1 = levels[0]
    z2 = levels[1]
  }
  if(!z2) {
    // no larger zoom found; use two largest for interpolation
    z1 = levels[levels.length-2]
    z2 = levels[levels.length-1]
  }

  if(z1 === z2) return // no two different zooms found; cannot interpolate

  // interpolate current horizontal/vertical angle of view
  const [h1, v1, d1, dx1, dy1] = zooms[z1]
  const [h2, v2, d2, dx2, dy2] = zooms[z2]
  const ha = (h1-h2) / (z1-z2)
  const hb = h1 - ha*z1
  const va = (v1-v2) / (z1-z2)
  const vb = v1 - va*z1
  const da = (d1-d2) / (z1-z2)
  const db = d1 - da*z1
  const h_angle = Algo.roundBy(zoom * ha + hb, 2)
  const v_angle = Algo.roundBy(zoom * va + vb, 2)
  const d_angle = Algo.roundBy(zoom * da + db, 2)
  const angles = { z1, z2, zoom, h_angle, v_angle }
  if(!isNaN(d_angle)) angles.d_angle = d_angle

  const dxa = (dx1-dx2) / (z1-z2)
  const dxb = dx1 - dxa*z1
  const dx = Math.round(zoom * dxa + dxb)
  if(!isNaN(dx)) angles.dx = dx

  const dya = (dy1-dy2) / (z1-z2)
  const dyb = dy1 - dya*z1
  const dy = Math.round(zoom * dya + dyb)
  if(!isNaN(dy)) angles.dy = dy

  return angles
} // anglesForZoom

Algo.vanishingPoint = function(rotationMatrix, cameraMatrix, round = false, cameraHeight = 1) {
  const p1 = Algo.project([1, 1, -cameraHeight], rotationMatrix, cameraMatrix, false)
  const p2 = Algo.project([1000000, 1, -cameraHeight], rotationMatrix, cameraMatrix, false)
  const p3 = Algo.project([1, 8, -cameraHeight], rotationMatrix, cameraMatrix, false)
  const p4 = Algo.project([1000000, 8, -cameraHeight], rotationMatrix, cameraMatrix, false)
  let [x, y] = Algo.intersection([...p1, ...p2], [...p3, ...p4])

  if(round) {
    x = Math.round(x)
    y = Math.round(y)
  }

  return [x, y]
} // vanishingPoint

Algo.project = function(point, rotationMatrix, cameraMatrix, round) {
  // typical use: point = [longitudal, -lateral, -camera height]
  // Vehicle Coordinate System XYZ / RPY
  // yaw (Z) is the rotation around the vertical axis (steering left-right)
  //    typical is 20 degrees; positive when camera looks to the left
  // pitch (Y) is the rotation around the lateral axis (nose goes up-down)
  //    typical is 0; positive when camera looks up
  // roll (X) is the rotation around the longitudinal (leaning left-right)
  //    desired to be 0; positive when camera tilts to the left

  // Transform the 3D point to 2D
  const [X, Y, Z] = point // [Longitudal, Lateral, Height]
  const [[a, b, c], [d, e, f], [g, h, i]] = rotationMatrix
  const [[f_x, , h_px_half], [, f_y, v_px_half]] = cameraMatrix

  const dForward = a*X + b*Y + c*Z
  const dRight = d*X + e*Y + f*Z
  const dUp = g*X + h*Y + i*Z
  let x = f_x * dRight/dForward + h_px_half
  let y = f_y * -dUp/dForward + v_px_half

  if(round) {
    x = Math.round(x)
    y = Math.round(y)
  }
  return [x, y]
} // project

Algo.projectPoints = function(points, rotationMatrix, cameraMatrix, round) {
  return points.map(point=>Algo.project(point, rotationMatrix, cameraMatrix, round))
} // projectPoints

Algo.unprojectX = function(point, LONGITUDAL, rotationMatrix, cameraMatrix, rounding) {
  // expects [x,y] and LONGITUDAL (X)
  // returns [X,Y,Z] = [LONGITUDAL, LATERAL, HEIGHT]

  const [x, y] = point
  const [[a, b, c], [d, e, f], [g, h, i]] = rotationMatrix
  const [[f_x, , h_px_half], [, f_y, v_px_half]] = cameraMatrix

  const scale = (x - h_px_half) / f_x
  const scaleY = (y - v_px_half) / f_y

  const A1 = e - scale * b
  const B1 = f - scale * c
  const C1 = -d * LONGITUDAL + scale * a * LONGITUDAL

  const A2 = h + scaleY * b
  const B2 = i + scaleY * c
  const C2 = -g * LONGITUDAL - scaleY * a * LONGITUDAL

  const det = A1 * B2 - A2 * B1
  if (Math.abs(det) < 1e-6) return null // Singular case

  let Y = (C1 * B2 - C2 * B1) / det
  let Z = (A1 * C2 - A2 * C1) / det

  if(rounding>=0) {
    Y = Algo.roundBy(Y, rounding)
    Z = Algo.roundBy(Z, rounding)
  }

  return [LONGITUDAL, Y, Z]
} // unprojectX

Algo.unprojectY = function(point, LATERAL, rotationMatrix, cameraMatrix, rounding) {
  // expects [x,y] and LATERAL (Y)
  // returns [X,Y,Z] = [LONGITUDAL, LATERAL, HEIGHT]

  const [x, y] = point
  const [[a, b, c], [d, e, f], [g, h, i]] = rotationMatrix
  const [[f_x, , h_px_half], [, f_y, v_px_half]] = cameraMatrix

  const scale = (x - h_px_half) / f_x
  const scaleY = (y - v_px_half) / f_y

  const A1 = d - scale * a
  const B1 = f - scale * c
  const C1 = -e * LATERAL + scale * b * LATERAL

  const A2 = g + scaleY * a
  const B2 = i + scaleY * c
  const C2 = -h * LATERAL - scaleY * b * LATERAL

  const det = A1 * B2 - A2 * B1
  if (Math.abs(det) < 1e-6) return null // Singular case

  let X = (C1 * B2 - C2 * B1) / det
  let Z = (A1 * C2 - A2 * C1) / det

  if(rounding>=0) {
    X = Algo.roundBy(X, rounding)
    Z = Algo.roundBy(Z, rounding)
  }

  return [X, LATERAL, Z]
} // unprojectY

Algo.unprojectZ = function(point, HEIGHT, rotationMatrix, cameraMatrix, rounding) {
  // expects [x,y] and HEIGHT (Z)
  // returns [X,Y,Z] = [LONGITUDAL, LATERAL, HEIGHT]
  // Vehicle Coordinate System XYZ / RPY
  // yaw (Z) is the rotation around the vertical axis (steering left-right)
  // pitch (Y) is the rotation around the lateral axis (nose goes up-down)
  // roll (X) is the rotation around the longitudinal (leaning left-right)

  const [x, y] = point
  const [[a, b, c], [d, e, f], [g, h, i]] = rotationMatrix
  const [[f_x, , h_px_half], [, f_y, v_px_half]] = cameraMatrix

  const scale = (x - h_px_half) / f_x
  const scaleY = (y - v_px_half) / f_y

  const A1 = d - scale * a
  const B1 = e - scale * b
  const C1 = scale * c * HEIGHT - f * HEIGHT

  const A2 = g + scaleY * a
  const B2 = h + scaleY * b
  const C2 = -scaleY * c * HEIGHT - i * HEIGHT

  const det = A1 * B2 - A2 * B1
  if (Math.abs(det) < 1e-6) return null // Singular case

  let X = (C1 * B2 - C2 * B1) / det
  let Y = (A1 * C2 - A2 * C1) / det

  if(rounding>=0) {
    X = Algo.roundBy(X, rounding)
    Y = Algo.roundBy(Y, rounding)
  }

  return [X, Y, HEIGHT]
} // unprojectZ

Algo.dataGauss1d = function(points, sigma=1, kernel_size) {
  function precomputeGaussianKernel(sigma, kernel_size) {
    const kernel = []
    let sum = 0
    for(let i=-kernel_size; i <= kernel_size; i++) {
      const weight = Math.exp(-(i * i) / (2 * sigma * sigma))
      kernel.push(weight)
      sum += weight
    }
    return kernel.map(w => w / sum)
  }

  if(!kernel_size) kernel_size = Math.ceil(3 * sigma)
  const kernel = precomputeGaussianKernel(sigma, kernel_size)
  const out = []
  for(let i = 0; i < points.length; i++) {
    let sum = 0; let weightSum = 0
    for(let k = -kernel_size; k <= kernel_size; k++) {
      let index = i + k
      index = Math.max(0, Math.min(points.length - 1, index))
      const weight = kernel[k + kernel_size]
      sum += points[index] * weight
      weightSum += weight
    }
    out.push(sum / weightSum)
  }
  return out
} // dataGauss1d

Algo.dataGauss2d = function(points, sigma=1, kernel_size) {
  function precomputeGaussianKernel(sigma, kernel_size) {
    const kernel = []
    let sum = 0
    for(let i=-kernel_size; i <= kernel_size; i++) {
      const weight = Math.exp(-(i * i) / (2 * sigma * sigma))
      kernel.push(weight)
      sum += weight
    }
    return kernel.map(w => w / sum)
  }

  if(!kernel_size) kernel_size = Math.ceil(3 * sigma)
  const kernel = precomputeGaussianKernel(sigma, kernel_size)
  const out = []
  for(let i = 0; i < points.length; i++) {
    let sumX = 0; let sumY = 0; let weightSum = 0
    for(let k = -kernel_size; k <= kernel_size; k++) {
      let index = i + k
      index = Math.max(0, Math.min(points.length - 1, index))
      const weight = kernel[k + kernel_size]
      sumX += points[index][0] * weight
      sumY += points[index][1] * weight
      weightSum += weight
    }
    out.push([sumX / weightSum, sumY / weightSum])
  }
  return out
} // dataGauss2d

Algo.derivation = function(points) {
  const derivatives = new Array(points.length)
  const n1 = points.length-1
  const n2 = points.length-2
  for(let i=1; i<n1; i++) {
    const dx = points[i + 1] - points[i - 1]
    derivatives[i] = dx
  }
  derivatives[0] = points[1] - points[0]
  derivatives[n1] = points[n1] - points[n2]

  return derivatives
} // derivation

Algo.derivation2d = function(points) {
  const derivatives = new Array(points.length)
  const n1 = points.length-1
  const n2 = points.length-2
  for(let i=1; i<n1; i++) {
    const dx = points[i + 1][0] - points[i - 1][0]
    const dy = points[i + 1][1] - points[i - 1][1]
    derivatives[i] = [points[i][0], dx !== 0 ? dy / dx : 0]
  }
  derivatives[0] = [points[0][0], (points[1][0] - points[0][0]) !== 0 ? (points[1][1] - points[0][1]) / (points[1][0] - points[0][0]) : 0]
  derivatives[n1] = [points[n1][0], (points[n1][0] - points[n2][0]) !== 0 ? (points[n1][1] - points[n2][1]) / (points[n1][0] - points[n2][0]) : 0]
  return derivatives
} // derivation2d

Algo.split = function(points, point_filter, group_filter) {
  const groups = []
  let open_group = []
  for(let i=0; i<points.length; i++) {
    const p = points[i]
    const ok = point_filter(p, i, points)
    if(ok) {
      open_group.push(p)
    }else {
      if(group_filter(open_group)) {
        groups.push(open_group)
      }
      open_group = []
    }
  }

  if(group_filter(open_group)) {
    groups.push(open_group)
  }

  return groups
} // split

Algo.splitIndexes = function(start_idx, end_idx, point_filter, group_filter) {
  const groups = []
  let open_group = []
  for(let i=start_idx; i<=end_idx; i++) {
    const ok = point_filter(i)
    if(ok) {
      open_group.push(i)
    }else {
      if(group_filter(open_group)) {
        groups.push(open_group)
      }
      open_group = []
    }
  }

  if(group_filter(open_group)) {
    groups.push(open_group)
  }

  return groups
} // splitIndexes

Algo.deskew = function(input, tlX, tlY, trX, trY, brX, brY, blX, blY, out_width, out_height) {
  const { width: in_width, height: in_height } = input
  const out = new Uint8ClampedArray(out_width*out_height)

  const dx1 = blX - tlX
  const dx2 = trX - tlX
  const dx3 = brX - blX
  const dx4 = brX - trX
  const dy1 = blY - tlY
  const dy2 = trY - tlY
  const dy3 = brY - blY
  const dy4 = brY - trY

  const c = tlX
  const f = tlY
  const g = (dx2*dy3 - dy2*dx3) / (dx4*dy3 - dy4*dx3) / out_width
  const h = (dx4*dy1 - dy4*dx1) / (dx4*dy3 - dy4*dx3) / out_height
  const a = trX*g + dx2/out_width
  const d = trY*g + dy2/out_width
  const b = blX*h + dx1/out_height
  const e = blY*h + dy1/out_height

  for(let y=0; y<out_height; y++) {
    for(let x=0; x<out_width; x++) {
      const x1 = a*x + b*y + c
      const y1 = d*x + e*y + f
      const z1 = g*x + h*y + 1
      const xx = Math.round(x1/z1)
      const yy = Math.round(y1/z1)
      if(xx>=0 && xx<in_width && yy>=0 && yy<in_height) { out[y*out_width+x] = input[yy*in_width+xx] }
    }
  }

  out.width = out_width
  out.height = out_height
  return out
} // deskew

Algo.rectOverlap = function(rect1, rect2) {
  let { x1, y1, x2, y2 } = rect1
  x1 = parseFloat(x1)
  y1 = parseFloat(y1)
  x2 = parseFloat(x2)
  y2 = parseFloat(y2)
  if(isNaN(x1)) x1 = parseFloat(rect1.left)
  if(isNaN(x1)) return false
  if(isNaN(y1)) y1 = parseFloat(rect1.top)
  if(isNaN(y1)) return false
  if(isNaN(x2)) x2 = x1 + parseFloat(rect1.width) - 1
  if(isNaN(x2)) return false
  if(isNaN(y2)) y2 = y1 + parseFloat(rect1.height) - 1
  if(isNaN(y2)) return false

  let { x1: _x1, y1: _y1, x2: _x2, y2: _y2 } = rect2
  _x1 = parseFloat(_x1)
  _y1 = parseFloat(_y1)
  _x2 = parseFloat(_x2)
  _y2 = parseFloat(_y2)
  if(isNaN(_x1)) _x1 = parseFloat(rect2.left)
  if(isNaN(_x1)) return false
  if(isNaN(_y1)) _y1 = parseFloat(rect2.top)
  if(isNaN(_y1)) return false
  if(isNaN(_x2)) _x2 = _x1 + parseFloat(rect2.width) - 1
  if(isNaN(_x2)) return false
  if(isNaN(_y2)) _y2 = _y1 + parseFloat(rect2.height) - 1
  if(isNaN(_y2)) return false

  ;[x1, x2] = [Math.min(x1, x2), Math.max(x1, x2)]
  ;[y1, y2] = [Math.min(y1, y2), Math.max(y1, y2)]
  ;[_x1, _x2] = [Math.min(_x1, _x2), Math.max(_x1, _x2)]
  ;[_y1, _y2] = [Math.min(_y1, _y2), Math.max(_y1, _y2)]

  if(_x1 > x2 || x1 > _x2) return false
  if(_y1 > y2 || y1 > _y2) return false
  return true
} // rectOverlap

Algo.insideRect = function(rect, x, y) {
  let { x1, y1, x2, y2 } = rect
  x1 = parseFloat(x1)
  y1 = parseFloat(y1)
  x2 = parseFloat(x2)
  y2 = parseFloat(y2)
  if(isNaN(x1)) x1 = parseFloat(rect.left)
  if(isNaN(x1)) return false
  if(isNaN(y1)) y1 = parseFloat(rect.top)
  if(isNaN(y1)) return false
  if(isNaN(x2)) x2 = x1 + parseFloat(rect.width) - 1
  if(isNaN(x2)) return false
  if(isNaN(y2)) y2 = y1 + parseFloat(rect.height) - 1
  if(isNaN(y2)) return false

  x = parseFloat(x)
  if(isNaN(x)) return false
  y = parseFloat(y)
  if(isNaN(y)) return false

  return(x>=x1 && x<=x2 && y>=y1 && y<=y2)
} // insideRect

const CLIP_INSIDE = 0 // 0000
const CLIP_LEFT = 1 // 0001
const CLIP_RIGHT = 2 // 0010
const CLIP_BOTTOM = 4 // 0100
const CLIP_TOP = 8 // 1000

Algo.clipLineOutCode = function(x, y, x_min, y_min, x_max, y_max) {
  let code = CLIP_INSIDE
  if(x < x_min) {
    // to the left of the rectangle
    code |= CLIP_LEFT
  }else if(x > x_max) {
    // to the right of the rectangle
    code |= CLIP_RIGHT
  }

  if(y < y_min) {
    // below the rectangle
    code |= CLIP_BOTTOM
  }else if(y > y_max) {
    // above the rectangle
    code |= CLIP_TOP
  }

  return code
} // clipLineOutCode

Algo.clipLine = function(x1, y1, x2, y2, x_min, y_min, x_max, y_max, round) {
  let outcode1 = Algo.clipLineOutCode(x1, y1, x_min, y_min, x_max, y_max)
  let outcode2 = Algo.clipLineOutCode(x2, y2, x_min, y_min, x_max, y_max)
  let accept = false

  while(true) {
    if(!(outcode1 | outcode2)) {
      // Both endpoints are inside the rectangle
      accept = true
      break
    } else if(outcode1 & outcode2) {
      // Both endpoints share an outside zone (completely outside)
      break
    } else {
      // At least one endpoint is outside the rectangle
      let x, y

      // Pick the outside point
      const outcodeOut = outcode1 || outcode2

      // Find the intersection point
      if(outcodeOut & CLIP_TOP) { // point is above the clip rectangle
        x = x1 + (x2 - x1) * (y_max - y1) / (y2 - y1)
        y = y_max
      } else if(outcodeOut & CLIP_BOTTOM) { // point is below the clip rectangle
        x = x1 + (x2 - x1) * (y_min - y1) / (y2 - y1)
        y = y_min
      } else if(outcodeOut & CLIP_RIGHT) { // point is to the right of clip rectangle
        y = y1 + (y2 - y1) * (x_max - x1) / (x2 - x1)
        x = x_max
      } else if(outcodeOut & CLIP_LEFT) { // point is to the left of clip rectangle
        y = y1 + (y2 - y1) * (x_min - x1) / (x2 - x1)
        x = x_min
      }

      // Now move outside point to the intersection point and recalculate outcode
      if(outcodeOut === outcode1) {
        x1 = x
        y1 = y
        outcode1 = Algo.clipLineOutCode(x1, y1, x_min, y_min, x_max, y_max)
      } else {
        x2 = x
        y2 = y
        outcode2 = Algo.clipLineOutCode(x2, y2, x_min, y_min, x_max, y_max)
      }
    }
  }

  if(accept) {
    if(round) {
      x1 = Math.round(x1)
      y1 = Math.round(y1)
      x2 = Math.round(x2)
      y2 = Math.round(y2)
    }
    return [x1, y1, x2, y2] // Clipped line coordinates
  }else {
    return null // Line is completely outside the clipping area
  }
} // clipLine

if(typeof module!=='undefined') { module.exports = Algo } else if(typeof window!=='undefined') { window.Algo = Algo }
